import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMessagesByContactIdWithPagination, sendMessage, sendFile, sendAudioMessage, sendGroupMessage, sendReaction } from '../services/MessageService';
import { getContacts, getContactsWithFilters, mapApiContactToContact, updateContact, createContact, deleteContact, archiveContact, toggleFavorite, getContactById, markContactAsViewed, DuplicateContactError, getMessages } from '../services/ContactService';
import type { Contact as ApiContact } from '../services/ContactService';
// NOTE: message loading now prefers /Contact/* /messages (and v1/Contact variants) + handles {status|message,code,data:{messages,total...}} via services (see MessageService + ContactService)

import { getAllUsers } from '../services/UserService';
import { getTags } from '../services/LabelService';
import { getWhatsAppGroups, WhatsAppGroupDto } from '../services/SectorService';
import { toggleContactAI } from '../services/AIAgentService';
import SessionService from '../services/SessionService';
import { AudioRecorder } from '../components/AudioRecorder';
import { ContactItem } from '../components/ContactItem';
import { AudioMessage } from '../components/AudioMessage';
import ChatWebSocketService from '../services/ChatWebSocketService';
import { Carousel } from '../components/Carousel';
import dayjs from 'dayjs';
import { isOperationalFilter, matchesOperationalFilter } from '../utils/chatOpFilters';
import { FiMoreVertical, FiUserPlus, FiTag, FiUsers, FiEdit2, FiArchive, FiCpu, FiMessageCircle, FiMenu, FiMic } from 'react-icons/fi';
import './ChatWeb.css';
import '../styles/components/ContactItem.css';
import { LoadingSpinner } from '../components/LoadingSpinner';
import ContactContextMenu from '../components/ContactContextMenu';
import InternalSystemMessage from '../components/InternalSystemMessage';
import { getInternalGroups, getContactsInGroup, addContactToGroup, removeContactFromGroup, getGroupsForContact, InternalGroup } from '../services/InternalGroupService';
import { ImSpinner2 } from 'react-icons/im';
import { FiX } from 'react-icons/fi';
import { ConfirmModal } from '../components/SharedModal';
import { useSector } from '../contexts/SectorContext';
import { rememberActiveContact } from '../mendsAi/lib/activeContact';
import { readChatLink } from '../utils/chatDeepLink';
import notificationService from '../services/NotificationService';
import { getContactPickerPrimaryLabel, isLidOnlyAddress, dedupeChatContactsForList, mergeChatListFirstPageFromPoll, compareChatContactsByRecencyDesc, chatContactsContentSignature, resolveContactDisplayName } from '../utils/formatters';
import { isOptimisticOutgoing, matchesOptimisticSent, matchesPendingOptimisticContent, markOptimisticAsSent } from '../utils/chatOptimisticMatch';
import { formatSparkOriginLabel, isSparkContact, pickSparkFieldsFromRealtime } from '../utils/sparkOriginLabel';
import { coalesceMediaUrl, enrichAudioMessageWithPendingBlob, getDisplayCaption } from '../utils/messageMedia';
import { Virtuoso } from 'react-virtuoso';
import {
  InboxAiAssist,
  ContactMemoryPanel,
  ConversationStatusBadge,
  resolveConversationStatus,
  isMendsVisionEnabled,
} from '../mendsAi';
import { openRegisterConversation } from '../mendsAi/openRegister';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import {
  EmailTimelineBubble,
  EmailReplyComposer,
  ReplyChannelSwitch,
  ChannelFilterSelect,
  useContactEmails,
  EmailHistoryNotice,
  mergeConversationTimeline,
  matchesChannelFilter,
  isEmailOnlyContact,
  channelFilterOptions,
  defaultReplyChannel,
  needsInstagramReconnect,
} from '../components/emailConversation';
import type { ConversationChannelFilter, ReplyChannel } from '../components/emailConversation';
import { ChannelBadge, MetaReplyComposer, InstagramReconnectNotice, WhatsAppWindowNotice, WhatsAppDeliveryNote, sendFailureText, useMetaChannels, useWebchatSite, metaMessageDisplay } from '../components/metaConversation';
import { whatsAppComposerLocked, WHATSAPP_TEMPLATE_ONLY_TEXT } from '../components/metaConversation/whatsAppWindow';
import type { ContactEmailItem } from '../services/ContactEmailService';

// Interfaces e tipos
interface OriginalContactData {
  id: number;
  name: string;
  tagId: number | null;
  number: string;
  avatarUrl: string | null;
  email: string;
  notes: string | null;
  sectorId: number;
  isActive: boolean;
  priority: string;
  contactStatus: 'Novo' | 'Qualificado' | 'Não qualificado' | string;
  aiActive: number;
  aiAgentName?: string | null;
  assignedTo: number | null;
  assignedToName?: string | null;
  createdAt: string;
  profilePicture?: string;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount?: number;
  is_viewed: boolean;
  archived?: boolean;
  isGroup?: boolean; // New field
  isFavorite?: boolean; // New field
  updatedAt?: string;
  sparkChatId?: string | null;
  sparkEntrypointId?: string | null;
  sparkLinePhone?: string | null;
  sparkLineLabel?: string | null;
  channel?: 'spark' | null;
  lastMessageChannel?: 'email' | 'whatsapp' | 'instagram' | 'messenger' | 'webchat' | null;
  hasEmail?: boolean;
  hasInstagram?: boolean;
  hasMessenger?: boolean;
  hasWebchat?: boolean;
  hasWhatsApp?: boolean;
}

interface Message {
  id: number;
  content: string | null;
  mediaType: string;
  mediaUrl: string | null;
  fileName: string | null;
  mimeType: string | null;
  sectorId: number;
  contactID: number;
  sentAt: string;
  isSent: boolean;
  isRead: boolean;
  status?: 'sending' | 'sent' | 'error';
  senderName?: string; // Name of sender in groups
  /** Instagram/Messenger: selo da conta e estado (apagada/editada). Ausentes no WhatsApp. */
  channel?: 'instagram' | 'messenger' | 'webchat';
  accountLabel?: string;
  /** deleted · edited (Meta) ou o estado de entrega do WhatsApp oficial: sent · delivered · read · played · failed. */
  state?: 'deleted' | 'edited' | 'sent' | 'delivered' | 'read' | 'played' | 'failed';
  /** Motivo em português quando o envio não foi entregue (WhatsApp oficial). */
  deliveryError?: string;
}

type ContactData = OriginalContactData;

const CHAT_CONTACTS_PAGE_SIZE = 50;

function normalizeGroupJidKey(value?: string | null): string {
  if (!value) return '';
  const v = value.trim().toLowerCase();
  if (v.includes('@g.us')) return v;
  const digits = v.replace(/\D/g, '');
  return digits || v;
}

function mapApiToContactData(c: ApiContact): ContactData {
  return {
    id: c.id,
    name: c.name,
    number: c.number,
    avatarUrl: c.avatarUrl,
    email: c.email,
    notes: c.notes,
    tagId: c.tagId,
    sectorId: c.sectorId,
    isActive: c.isActive,
    priority: c.priority,
    contactStatus: c.contactStatus,
    aiActive: c.aiActive,
    aiAgentName: c.aiAgentName ?? null,
    assignedTo: c.assignedTo,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    is_viewed: c.is_viewed ?? c.isViewed,
    archived: c.archived,
    isFavorite: c.isFavorite,
    lastMessage: c.lastMessage,
    lastMessageTime: c.lastMessageTime,
    unreadCount: c.unreadCount,
    isGroup: c.isGroup ?? false,
    sparkChatId: c.sparkChatId ?? null,
    sparkEntrypointId: c.sparkEntrypointId ?? null,
    sparkLinePhone: c.sparkLinePhone ?? null,
    sparkLineLabel: c.sparkLineLabel ?? null,
    channel: c.channel ?? null,
    lastMessageChannel: c.lastMessageChannel ?? null,
    hasEmail: c.hasEmail ?? false,
    hasInstagram: c.hasInstagram ?? false,
    hasMessenger: c.hasMessenger ?? false,
    hasWebchat: c.hasWebchat ?? false,
    hasWhatsApp: c.hasWhatsApp,
  };
}

type ActiveFilterType = string;

type ChatWebProps = {
  hasNewMessages?: { [contactId: number]: boolean };
  setHasNewMessages?: (newState: { [contactId: number]: boolean }) => void;
  contacts?: ContactData[];
  setContacts?: React.Dispatch<React.SetStateAction<ContactData[]>>;
  loadMoreContacts?: () => void;
  hasMoreContacts?: boolean;
  isLoadingMoreContacts?: boolean;
  totalContacts?: number;
  archivedCount?: number;
  favoritesCount?: number;
  unreadCount?: number;
  setUnreadCount?: (updater: number | ((prev: number) => number)) => void;
  whatsappGroupsCount?: number;
};

// Componentes de ícones
const ImageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14,2 14,8 20,8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10,9 9,9 8,9" />
  </svg>
);

const MicIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const ChatWeb: React.FC<ChatWebProps> = ({
  hasNewMessages: externalHasNewMessages,
  setHasNewMessages,
  contacts: externalContacts,
  setContacts: externalSetContacts,
  loadMoreContacts,
  hasMoreContacts,
  isLoadingMoreContacts,
  totalContacts: externalTotalContacts,
  archivedCount: externalArchivedCount,
  favoritesCount: externalFavoritesCount,
  unreadCount: externalUnreadCount,
  setUnreadCount,
  whatsappGroupsCount: externalWhatsappGroupsCount
}) => {
  // Estados principais
  const [selectedContact, setSelectedContact] = useState<ContactData | null>(null);
  const pendingDraftRef = useRef<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [contacts, setContacts] = useState<ContactData[]>(externalContacts || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatLoadingMore, setChatLoadingMore] = useState(false);
  const chatNextOffsetRef = useRef(0);
  const chatLoadLockRef = useRef(false);
  const [activeFilter, setActiveFilter] = useState<ActiveFilterType>('all');
  const { selectedSectorId } = useSector();
  // E-mail em Conversas: filtro por canal da lista, canal da resposta e e-mail respondido.
  const [channelFilter, setChannelFilter] = useState<ConversationChannelFilter>('all');
  const [replyChannel, setReplyChannel] = useState<ReplyChannel>('whatsapp');
  const [emailReplyTo, setEmailReplyTo] = useState<ContactEmailItem | null>(null);
  const [emailDraftSeed, setEmailDraftSeed] = useState<string | undefined>(undefined);
  const [isRecording, setIsRecording] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [hasNewMessages, setLocalHasNewMessages] = useState<{ [contactId: number]: boolean }>(externalHasNewMessages || {});
  const [showMemoryPanel, setShowMemoryPanel] = useState(false);
  const visionEnabled = isMendsVisionEnabled();
  const contactEmails = useContactEmails(
    selectedSectorId ? Number(selectedSectorId) : null,
    selectedContact && !selectedContact.isGroup ? selectedContact.id : null,
  );
  const emailOnly = isEmailOnlyContact(selectedContact);
  const emailAvailable = !contactEmails.forbidden && (contactEmails.status?.connected ?? false);
  // Instagram: só quando o setor tem conta ativa e o cliente tem a identidade do Instagram.
  const { instagramActive, messengerActive, whatsAppCloudActive } = useMetaChannels(selectedSectorId ? Number(selectedSectorId) : null);
  // Setor Cloud com a janela de 24 h fechada: texto, mídia e voz ficam travados (o servidor daria 409); só vale "Enviar modelo".
  const waComposerLocked = whatsAppComposerLocked({
    active: whatsAppCloudActive && replyChannel === 'whatsapp' && !selectedContact?.isGroup,
    messages,
    loading: isMessagesLoading,
  });
  const instagramAvailable = instagramActive && selectedContact?.hasInstagram === true;
  // Messenger: Página ativa no setor e o cliente com a identidade (PSID) dela.
  const messengerAvailable = messengerActive && selectedContact?.hasMessenger === true;
  // Site: o filtro só aparece com site ativo; a resposta vale sempre que o cliente falou pelo site.
  const { webchatActive } = useWebchatSite(selectedSectorId ? Number(selectedSectorId) : null);
  const webchatAvailable = selectedContact?.hasWebchat === true;
  const whatsappAvailable = selectedContact?.hasWhatsApp ?? Boolean((selectedContact?.number || '').trim());
  // Só escreve pelo Instagram e a conta caiu: faixa de reconexão no lugar do compositor do WhatsApp.
  const instagramNeedsReconnect = needsInstagramReconnect(selectedContact, { instagram: instagramActive });
  const timeline = useMemo(
    () => mergeConversationTimeline(messages, contactEmails.forbidden ? [] : contactEmails.emails),
    [messages, contactEmails.emails, contactEmails.forbidden],
  );

  // Conversa nova: cliente só de e-mail abre na resposta por e-mail; os demais no WhatsApp.
  useEffect(() => {
    setReplyChannel(defaultReplyChannel(selectedContact, { instagram: instagramAvailable, messenger: messengerAvailable }));
    setEmailReplyTo(null);
    setEmailDraftSeed(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact?.id, emailOnly, instagramAvailable, messengerAvailable, webchatAvailable]);

  const handleReplyByEmail = useCallback((email: ContactEmailItem) => {
    setEmailReplyTo(email);
    setReplyChannel('email');
  }, []);

  const skipDeselectOnFilterRef = useRef(false);
  /** Evita loop quando empurramos a lista completa para o CombinedMenu (pai). */
  const skipExternalContactsMergeRef = useRef(false);
  const externalSetContactsRef = useRef(externalSetContacts);
  useEffect(() => {
    externalSetContactsRef.current = externalSetContacts;
  }, [externalSetContacts]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Sync primeiro bloco do menu (polling 5s): atualiza linhas existentes e insere contactos novos da 1ª página; mantém páginas já carregadas abaixo.
  useEffect(() => {
    if (!externalContacts?.length) return;
    if (skipExternalContactsMergeRef.current) {
      skipExternalContactsMergeRef.current = false;
      return;
    }
    setContacts(prev => {
      const merged = mergeChatListFirstPageFromPoll(prev, externalContacts as ContactData[]);
      // Se o poll não trouxe nenhuma mudança de conteúdo, mantém a MESMA referência para o React
      // pular o re-render (evita a lista repintar de 5 em 5s à toa). A ordem é imposta na renderização.
      if (chatContactsContentSignature(prev) === chatContactsContentSignature(merged)) return prev;
      return merged;
    });
  }, [externalContacts]);

  // Mantém no CombinedMenu a mesma lista que o chat (incl. scroll infinito), para não perder contatos ao sair p/ broadcast/outras rotas.
  useEffect(() => {
    const push = externalSetContactsRef.current;
    if (!push) return;
    const ext = (externalContacts || []) as ContactData[];
    const sigC = contacts.map(c => c.id).join(',');
    const sigE = ext.map((c: ContactData) => c.id).join(',');
    if (contacts.length === ext.length && sigC === sigE) return;
    skipExternalContactsMergeRef.current = true;
    push(contacts);
  }, [contacts, externalContacts]);

  // Quando a lista de contatos é atualizada pelo polling (ex.: IA transferiu para humano), manter selectedContact em sync com aiActive
  useEffect(() => {
    if (!selectedContact || !externalContacts?.length) return;
    const fromList = externalContacts.find((c: any) => c.id === selectedContact.id) as ContactData | undefined;
    if (!fromList) return;
    const listAi = fromList.aiActive;
    const spark = pickSparkFieldsFromRealtime(fromList as unknown as Record<string, unknown>);
    const aiChanged = listAi !== undefined && listAi !== selectedContact.aiActive;
    const listAgent = fromList.aiAgentName ?? null;
    const agentChanged = listAgent !== (selectedContact.aiAgentName ?? null);
    const sparkChanged =
      (spark.channel ?? null) !== (selectedContact.channel ?? null) ||
      (spark.sparkChatId ?? null) !== (selectedContact.sparkChatId ?? null) ||
      (spark.sparkLinePhone ?? null) !== (selectedContact.sparkLinePhone ?? null) ||
      (spark.sparkLineLabel ?? null) !== (selectedContact.sparkLineLabel ?? null);
    if (!aiChanged && !sparkChanged && !agentChanged) return;
    setSelectedContact(prev =>
      prev
        ? {
            ...prev,
            ...(aiChanged ? { aiActive: listAi } : {}),
            aiAgentName: listAgent,
            channel: spark.channel ?? prev.channel,
            sparkChatId: spark.sparkChatId ?? prev.sparkChatId,
            sparkEntrypointId: spark.sparkEntrypointId ?? prev.sparkEntrypointId,
            sparkLinePhone: spark.sparkLinePhone ?? prev.sparkLinePhone,
            sparkLineLabel: spark.sparkLineLabel ?? prev.sparkLineLabel,
          }
        : null
    );
  }, [externalContacts, selectedContact?.id, selectedContact?.aiActive, selectedContact?.channel, selectedContact?.sparkChatId, selectedContact?.sparkLinePhone]);

  // Sincronizar counts externos (vindos do polling do CombinedMenu)
  useEffect(() => {
    if (externalArchivedCount !== undefined) {
      setArchivedContactsCount(externalArchivedCount);
    }
  }, [externalArchivedCount]);

  useEffect(() => {
    if (externalFavoritesCount !== undefined) {
      setFavoritesCount(externalFavoritesCount);
    }
  }, [externalFavoritesCount]);

  useEffect(() => {
    if (externalTotalContacts !== undefined) {
      setTotalContactsCount(externalTotalContacts);
    }
  }, [externalTotalContacts]);

  useEffect(() => {
    if (externalHasNewMessages) {
      setLocalHasNewMessages(externalHasNewMessages);
    }
  }, [externalHasNewMessages]);

  // Ao mudar de aba/filtro, fechar o chat e voltar para a lista (exceto deep link que ajusta filtro + seleção)
  useEffect(() => {
    if (skipDeselectOnFilterRef.current) {
      skipDeselectOnFilterRef.current = false;
      return;
    }
    setSelectedContact(null);
  }, [activeFilter]);

  // Estados para funcionalidades
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [isTransferLoading, setIsTransferLoading] = useState(false);
  const [isTagLoading, setIsTagLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [currentUserId, setCurrentUserId] = useState<number | undefined>(SessionService.getCurrentUserId() || undefined);
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(SessionService.isCurrentUserAdmin());

  // Estados para filtros e modais
  // const [showArchivedContacts, setShowArchivedContacts] = useState(false); // Substituído por activeFilter
  const [archivedContactsCount, setArchivedContactsCount] = useState(0);
  const [totalContactsCount, setTotalContactsCount] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [showNewContactModal, setShowNewContactModal] = useState(false);


  const [newContactName, setNewContactName] = useState('');
  const [newContactNumber, setNewContactNumber] = useState('');
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [newContactError, setNewContactError] = useState('');
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [selectedFilterUser, setSelectedFilterUser] = useState<number | null>(null);
  const [selectedFilterTag, setSelectedFilterTag] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingContact, setIsDeletingContact] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isArchivingContact, setIsArchivingContact] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  // Estados para microfone
  const [isMicrophoneAvailable, setIsMicrophoneAvailable] = useState(false);
  const [isCheckingMicrophone, setIsCheckingMicrophone] = useState(true);

  // Estado para AI toggle
  const [isTogglingAI, setIsTogglingAI] = useState(false);

  // Refs
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadingRef = useRef(false);
  /** Incrementado a cada troca de contato / fetch — evita que load antigo sobrescreva o chat aberto. */
  const messagesLoadSeqRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [scrollButtonLeft, setScrollButtonLeft] = useState<number | null>(null);
  const shouldScrollToBottomRef = useRef(false);
  const isUserScrollingRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const pendingMessagesRef = useRef<Map<number, { mediaType: string; sentAt: string }>>(new Map());
  const selectedContactRef = useRef<ContactData | null>(null);

  // Sync ref with state
  useEffect(() => {
    selectedContactRef.current = selectedContact;
  }, [selectedContact]);

  const mapRealtimePayloadToMessage = useCallback((data: any, fallbackContactId?: number): Message | null => {
    const id = Number(data?.id);
    const contactID = Number(data?.contactID ?? data?.contactId ?? fallbackContactId);
    if (!id || !contactID) return null;
    return {
      id,
      content: data.content ?? null,
      mediaType: (data.mediaType ?? data.media_type ?? 'text').toLowerCase(),
      mediaUrl: data.mediaUrl ?? data.media_url ?? null,
      fileName: data.fileName ?? data.file_name ?? null,
      mimeType: data.mimeType ?? data.mime_type ?? null,
      sectorId: Number(data.sectorId ?? data.sector_id ?? selectedSectorId ?? 0),
      contactID,
      sentAt: data.sentAt ?? data.sent_at ?? data.created_at ?? new Date().toISOString(),
      isSent: Boolean(data.isSent ?? data.is_sent ?? false),
      isRead: Boolean(data.isRead ?? data.is_read ?? false),
      senderName: data.senderName ?? data.sender_name ?? undefined,
      // O evento em tempo real só traz o canal; o selo com a conta chega na próxima leitura da conversa.
      channel: data.channel === 'instagram' || data.channel === 'messenger' || data.channel === 'webchat' ? data.channel : undefined,
      status: 'sent',
    };
  }, [selectedSectorId]);

  const mergeRealtimeMessage = useCallback((incoming: Message) => {
    setMessages(prev => {
      if (prev.some(m => m.id === incoming.id)) return prev;
      const withoutMatchedOptimistic = prev.filter(m => !matchesOptimisticSent(m, incoming));
      return [...withoutMatchedOptimistic, incoming]
        .sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf());
    });
    shouldScrollToBottomRef.current = true;
  }, []);

  useEffect(() => {
    const handleRealtimeMessage = (data: any, isGroupHint = false) => {
      const msg = mapRealtimePayloadToMessage(data);
      if (!msg) return;

      setContacts(prev => {
        const preview = msg.mediaType !== 'text' ? `[${msg.mediaType}]` : (msg.content ?? '');
        const spark = pickSparkFieldsFromRealtime(data);
        const exists = prev.some(c => c.id === msg.contactID);
        const promoteName = !msg.isSent && !!msg.senderName;
        const updated = exists
          ? prev.map(c =>
              c.id === msg.contactID
                ? {
                    ...c,
                    name: promoteName
                      ? resolveContactDisplayName(c.name, msg.senderName, c.number)
                      : c.name,
                    lastMessage: preview || c.lastMessage,
                    lastMessageTime: msg.sentAt,
                    updatedAt: msg.sentAt,
                    channel: spark.channel ?? c.channel,
                    sparkChatId: spark.sparkChatId ?? c.sparkChatId,
                    sparkEntrypointId: spark.sparkEntrypointId ?? c.sparkEntrypointId,
                    sparkLinePhone: spark.sparkLinePhone ?? c.sparkLinePhone,
                    sparkLineLabel: spark.sparkLineLabel ?? c.sparkLineLabel,
                  }
                : c
            )
          : [{
              id: msg.contactID,
              name: resolveContactDisplayName(undefined, msg.senderName, undefined),
              number: '',
              avatarUrl: null,
              email: '',
              notes: '',
              tagId: null,
              sectorId: msg.sectorId,
              isActive: true,
              // Deriva do payload/evento — NUNCA cravar false, senão um grupo entra como contato
              // e fica "piscando" entre grupo e contato conforme realtime × poll.
              isGroup: Boolean(data?.isGroup ?? data?.is_group ?? isGroupHint),
              contactStatus: 'active' as const,
              priority: 'medium' as const,
              aiActive: 1,
              assignedTo: null,
              createdAt: msg.sentAt,
              lastMessage: preview,
              lastMessageTime: msg.sentAt,
              updatedAt: msg.sentAt,
              is_viewed: false,
              ...spark,
            }, ...prev];
        return updated.sort(compareChatContactsByRecencyDesc);
      });

      if (selectedContactRef.current?.id === msg.contactID) {
        const spark = pickSparkFieldsFromRealtime(data);
        setSelectedContact(prev =>
          prev
            ? {
                ...prev,
                name: !msg.isSent
                  ? resolveContactDisplayName(prev.name, msg.senderName, prev.number)
                  : prev.name,
                channel: spark.channel ?? prev.channel,
                sparkChatId: spark.sparkChatId ?? prev.sparkChatId,
                sparkEntrypointId: spark.sparkEntrypointId ?? prev.sparkEntrypointId,
                sparkLinePhone: spark.sparkLinePhone ?? prev.sparkLinePhone,
                sparkLineLabel: spark.sparkLineLabel ?? prev.sparkLineLabel,
              }
            : prev
        );
        mergeRealtimeMessage(msg);
      } else if (!msg.isSent) {
        setLocalHasNewMessages(prev => ({ ...prev, [msg.contactID]: true }));
      }
    };

    const handleContactAiStatus = (data: any) => {
      const contactId = Number(data?.contactId ?? data?.contactID);
      const aiActive = Number(data?.aiActive ?? (data?.ai_active ? 1 : 0));
      if (!contactId) return;
      setContacts(prev => prev.map(c => (c.id === contactId ? { ...c, aiActive } : c)));
      setSelectedContact(prev => (prev?.id === contactId ? { ...prev, aiActive } : prev));
    };

    const handleContactUpdate = (data: any) => {
      const contactId = Number(data?.id);
      if (!contactId) return;
      const spark = pickSparkFieldsFromRealtime(data);
      const patchSpark = (c: ContactData): ContactData => ({
        ...c,
        number: (data.number as string | undefined) || c.number,
        channel: spark.channel ?? c.channel,
        sparkChatId: spark.sparkChatId ?? c.sparkChatId,
        sparkEntrypointId: spark.sparkEntrypointId ?? c.sparkEntrypointId,
        sparkLinePhone: spark.sparkLinePhone ?? c.sparkLinePhone,
        sparkLineLabel: spark.sparkLineLabel ?? c.sparkLineLabel,
      });
      setSelectedContact(prev => {
        if (prev?.id !== contactId) return prev;
        const patched = patchSpark(prev);
        return {
          ...patched,
          name: resolveContactDisplayName(prev.name, data.name, patched.number),
        };
      });
      setContacts(prev => {
        const exists = prev.some(c => c.id === contactId);
        if (!exists) {
          const sectorId = Number(data?.sectorId ?? data?.sector_id ?? selectedSectorId ?? 0);
          const number = data.number ?? '';
          const incoming: ContactData = {
            id: contactId,
            name: resolveContactDisplayName(undefined, data.name, number),
            number,
            avatarUrl: data.avatarUrl ?? data.avatar_url ?? null,
            email: '',
            notes: '',
            tagId: null,
            sectorId,
            isActive: true,
            isGroup: Boolean(data.isGroup ?? data.is_group ?? false),
            contactStatus: 'active',
            priority: 'medium',
            aiActive: Number(data.aiActive ?? data.ai_active ?? 1),
            assignedTo: null,
            createdAt: data.createdAt ?? data.created_at ?? new Date().toISOString(),
            lastMessage: data.lastMessage ?? data.last_message ?? undefined,
            lastMessageTime: data.lastMessageTime ?? data.last_message_time ?? data.updatedAt,
            updatedAt: data.updatedAt ?? data.updated_at,
            is_viewed: false,
            ...spark,
          };
          return [incoming, ...prev];
        }
        return prev.map(c =>
          c.id === contactId
            ? {
                ...patchSpark(c),
                name: resolveContactDisplayName(c.name, data.name, data.number ?? c.number),
                aiActive: data.aiActive ?? data.ai_active ?? c.aiActive,
                lastMessage: data.lastMessage ?? data.last_message ?? c.lastMessage,
                lastMessageTime: data.lastMessageTime ?? data.last_message_time ?? c.lastMessageTime,
                updatedAt: data.updatedAt ?? data.updated_at ?? c.updatedAt,
              }
            : c
        );
      });
    };

    const unsubMessage = ChatWebSocketService.on('message', (data: any) => handleRealtimeMessage(data, false));
    const unsubGroup = ChatWebSocketService.on('group_message', (data: any) => handleRealtimeMessage(data, true));
    const unsubAi = ChatWebSocketService.on('contact_ai_status', handleContactAiStatus);
    const unsubContact = ChatWebSocketService.on('contact', handleContactUpdate);
    const unsubTransfer = ChatWebSocketService.on('human_transfer_notification', (data: any) => {
      const contactId = Number(data?.contactId ?? data?.contactID);
      if (!contactId) return;
      setContacts(prev => prev.map(c => (c.id === contactId ? { ...c, aiActive: 0 } : c)));
      setSelectedContact(prev => (prev?.id === contactId ? { ...prev, aiActive: 0 } : prev));
    });

    return () => {
      unsubMessage();
      unsubGroup();
      unsubAi();
      unsubContact();
      unsubTransfer();
    };
  }, [mapRealtimePayloadToMessage, mergeRealtimeMessage, selectedSectorId]);

  // Group Management State
  const [internalGroups, setInternalGroups] = useState<InternalGroup[]>([]);
  const [whatsappGroups, setWhatsappGroups] = useState<WhatsAppGroupDto[]>([]);
  const whatsappGroupsRef = useRef(whatsappGroups);
  useEffect(() => {
    whatsappGroupsRef.current = whatsappGroups;
  }, [whatsappGroups]);
  const [groupMembers, setGroupMembers] = useState<{ [groupId: number]: number[] }>({});
  const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number; contactId: number | null }>({ visible: false, x: 0, y: 0, contactId: null });
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedContactForGroup, setSelectedContactForGroup] = useState<OriginalContactData | null>(null);
  const [contactGroups, setContactGroups] = useState<number[]>([]); // IDs of groups the selected contact is in

  // Hooks
  const location = useLocation();
  const navigate = useNavigate();

  // Constantes
  const MESSAGES_PAGE_SIZE = 50;

  // Função para atualizar hasNewMessages
  const updateHasNewMessages = useCallback((newState: { [contactId: number]: boolean } | ((prev: { [contactId: number]: boolean }) => { [contactId: number]: boolean })) => {
    if (typeof newState === 'function') {
      setLocalHasNewMessages(newState);
      if (setHasNewMessages) {
        setHasNewMessages(newState(hasNewMessages));
      }
    } else {
      setLocalHasNewMessages(newState);
      if (setHasNewMessages) {
        setHasNewMessages(newState);
      }
    }
  }, [setHasNewMessages, hasNewMessages]);

  // Função para normalizar data
  function normalizeSentAtToLocal(sentAt: string) {
    const date = dayjs(sentAt);
    return date.isValid() ? date.toISOString() : dayjs().toISOString();
  }

  // Função para formatar tempo da mensagem
  function formatMessageTime(sentAt: string, isLocalMessage?: boolean) {
    try {
      const messageTime = dayjs(sentAt);
      if (!messageTime.isValid()) {
        return dayjs().format('HH:mm');
      }
      return messageTime.format('HH:mm');
    } catch (error) {
      console.error('Erro ao formatar tempo da mensagem:', error);
      return dayjs().format('HH:mm');
    }
  }

  // Carregar mensagens
  const loadMessages = useCallback(async (contactId: number, pageParam = 0, append = false, isGroup = false) => {
    // Só bloqueia paginação (scroll up); carga inicial sempre roda (corrige chat vazio ao trocar contato).
    if (append && loadingRef.current) return;

    const seq = ++messagesLoadSeqRef.current;
    loadingRef.current = true;
    if (append) setIsLoadingMore(true);
    else if (pageParam === 0) setIsMessagesLoading(true);

    let previousScrollHeight = 0;
    let previousScrollTop = 0;
    if (append && messagesContainerRef.current) {
      previousScrollHeight = messagesContainerRef.current.scrollHeight;
      previousScrollTop = messagesContainerRef.current.scrollTop;
    }

    try {
      const offset = pageParam * MESSAGES_PAGE_SIZE;
      const sectorId = Number(SessionService.getSectorId());

      let newMessages: Message[] = [];
      let hasMoreRes = false;

      const response = await getMessagesByContactIdWithPagination(contactId, MESSAGES_PAGE_SIZE, offset);
      if (seq !== messagesLoadSeqRef.current) return;

      newMessages = response.messages.map(msg => ({
        ...msg,
        mediaUrl: coalesceMediaUrl(msg as Message & Record<string, unknown>) ?? msg.mediaUrl,
        sentAt: normalizeSentAtToLocal(msg.sentAt)
      })) || [];
      hasMoreRes = response.hasMore;

      if (!append && response.contact) {
        const spark = pickSparkFieldsFromRealtime(response.contact as unknown as Record<string, unknown>);
        setSelectedContact(prev =>
          prev && prev.id === contactId
            ? {
                ...prev,
                number: response.contact?.number || prev.number,
                channel: spark.channel ?? prev.channel,
                sparkChatId: spark.sparkChatId ?? prev.sparkChatId,
                sparkEntrypointId: spark.sparkEntrypointId ?? prev.sparkEntrypointId,
                sparkLinePhone: spark.sparkLinePhone ?? prev.sparkLinePhone,
                sparkLineLabel: spark.sparkLineLabel ?? prev.sparkLineLabel,
              }
            : prev
        );
      }

      setHasMore(hasMoreRes);

      if (append) {
        setMessages(prev => {
          if (seq !== messagesLoadSeqRef.current) return prev;
          const updatedMessages = [
            ...newMessages,
            ...prev
          ].sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf());

          setTimeout(() => {
            if (messagesContainerRef.current && previousScrollHeight > 0) {
              const newScrollHeight = messagesContainerRef.current.scrollHeight;
              const scrollDifference = newScrollHeight - previousScrollHeight;
              messagesContainerRef.current.scrollTop = previousScrollTop + scrollDifference;
            }
          }, 100);

          return updatedMessages;
        });
      } else {
        setMessages(
          newMessages.sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf())
        );
      }
      setInitialLoad(false);
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    } finally {
      if (seq === messagesLoadSeqRef.current) {
        if (append) setIsLoadingMore(false);
        else if (pageParam === 0) setIsMessagesLoading(false);
        loadingRef.current = false;
      }
    }
  }, [setMessages, setHasMore]);

  // Função melhorada para scroll para baixo
  const scrollToBottom = useCallback((behavior: 'smooth' | 'instant' = 'smooth') => {
    const scrollBehavior: ScrollBehavior = behavior === 'instant' ? 'auto' : behavior;
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      // Aguardar um pouco para garantir que o DOM foi atualizado
      requestAnimationFrame(() => {
        messagesContainer.scrollTo({
          top: messagesContainer.scrollHeight,
          behavior: scrollBehavior
        });
      });
    }
  }, []);

  // Função para verificar se deve fazer scroll automático
  const shouldAutoScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return false;

    // Se é carregamento inicial, sempre fazer scroll
    if (initialLoad) return true;

    // Se o usuário não está fazendo scroll manual, verificar se está próximo do final
    if (!isUserScrollingRef.current) {
      const threshold = 100;
      const isNearBottom = container.scrollTop >= (container.scrollHeight - container.clientHeight - threshold);
      return isNearBottom;
    }

    return false;
  }, [initialLoad]);

  // Função para aguardar carregamento de imagens
  const waitForImagesLoad = useCallback(async () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const images = container.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();

      return new Promise<void>((resolve) => {
        const onLoad = () => {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onLoad);
          resolve();
        };
        img.addEventListener('load', onLoad);
        img.addEventListener('error', onLoad);

        // Timeout para não travar indefinidamente
        setTimeout(onLoad, 3000);
      });
    });

    await Promise.all(imagePromises);
  }, []);

  // Função para fazer scroll inteligente
  const smartScrollToBottom = useCallback(async (force = false) => {
    if (!force && !shouldAutoScroll()) return;

    // Aguardar carregamento de imagens
    await waitForImagesLoad();

    // Fazer scroll
    scrollToBottom(initialLoad ? 'instant' : 'smooth');

    // Marcar que não é mais carregamento inicial
    if (initialLoad) {
      setInitialLoad(false);
    }
  }, [shouldAutoScroll, waitForImagesLoad, scrollToBottom, initialLoad]);

  // Enviar arquivo
  const handleFileUpload = async (file: File) => {
    if (!selectedContact || !selectedSectorId) return;

    const tempId = Date.now();
    const contactId = selectedContact.id;
    let nowLocal = dayjs();

    if (messages.length > 0) {
      const maxSentAt = messages.reduce((max, m) => {
        const t = dayjs(m.sentAt).valueOf();
        return t > max ? t : max;
      }, 0);
      if (nowLocal.valueOf() <= maxSentAt) {
        nowLocal = dayjs(maxSentAt + 1000);
      }
    }

    const sentAtString = nowLocal.format();

    // Marcar que deve fazer scroll
    shouldScrollToBottomRef.current = true;

    const mediaType = file.type.startsWith('image/') ? 'image' :
      file.type.startsWith('video/') ? 'video' :
        file.type.startsWith('audio/') ? 'audio' : 'document';

    // Create local preview URL for images and videos
    const localPreviewUrl = (mediaType === 'image' || mediaType === 'video')
      ? URL.createObjectURL(file)
      : null;

    // Optimistic update - add message immediately with 'sending' status
    const optimisticMessage: Message = {
      id: tempId,
      content: file.name,
      mediaType: mediaType,
      mediaUrl: localPreviewUrl,
      fileName: file.name,
      mimeType: file.type,
      sectorId: Number(selectedSectorId),
      contactID: contactId,
      sentAt: sentAtString,
      isSent: true,
      isRead: false,
      status: 'sending'
    };

    // Track this pending message for WebSocket matching
    pendingMessagesRef.current.set(tempId, { mediaType, sentAt: sentAtString });

    setMessages(prev => [...prev, optimisticMessage]);

    // Optimistic update - update contact list last message
    setContacts(prev => {
      return prev.map(c => {
        if (c.id === contactId) {
          return {
            ...c,
            lastMessage: mediaType === 'image' ? 'Imagem' : (mediaType === 'video' ? 'Vídeo' : 'Documento'),
            lastMessageTime: sentAtString,
            updatedAt: sentAtString
          };
        }
        return c;
      }).sort((a, b) => {
        const timeA = a.lastMessageTime ? dayjs(a.lastMessageTime).valueOf() : 0;
        const timeB = b.lastMessageTime ? dayjs(b.lastMessageTime).valueOf() : 0;
        return timeB - timeA;
      });
    });

    try {
      await sendFile({
        file,
        fileName: file.name,
        mediaType,
        caption: '',
        recipient: selectedContact.number,
        recipientPhone: selectedContact.number,
        contactId: selectedContact.id,
        sectorId: Number(selectedSectorId),
        isHuman: true,
        userId: currentUserId,
        isAnonymous: false,
        isSparkContact: isSparkContact(selectedContact)
      });
      setMessages(prev => markOptimisticAsSent(prev, tempId));
      pendingMessagesRef.current.delete(tempId);
    } catch (error) {
      console.error('Erro ao enviar arquivo:', error);
      // Update message status to 'error'
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, status: 'error' as const, deliveryError: sendFailureText(error) } : m
      ));
    }
  };

  // Manipular seleção de arquivo
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Limpar o input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Enviar áudio
  const handleSendAudio = async (audioBlob: Blob) => {
    if (!selectedContact || !selectedSectorId) return;

    const tempId = Date.now();
    const contactId = selectedContact.id;
    let nowLocal = dayjs();

    if (messages.length > 0) {
      const maxSentAt = messages.reduce((max, m) => {
        const t = dayjs(m.sentAt).valueOf();
        return t > max ? t : max;
      }, 0);
      if (nowLocal.valueOf() <= maxSentAt) {
        nowLocal = dayjs(maxSentAt + 1000);
      }
    }

    const sentAtString = nowLocal.format();

    // Create local preview URL for audio
    const localAudioUrl = URL.createObjectURL(audioBlob);

    // Marcar que deve fazer scroll
    shouldScrollToBottomRef.current = true;
    setIsRecording(false);

    // Optimistic update - add audio message immediately with 'sending' status
    const optimisticMessage: Message = {
      id: tempId,
      content: null,
      mediaType: 'audio',
      mediaUrl: localAudioUrl,
      fileName: `audio_${tempId}.wav`,
      mimeType: 'audio/wav',
      sectorId: Number(selectedSectorId),
      contactID: contactId,
      sentAt: sentAtString,
      isSent: true,
      isRead: false,
      status: 'sending'
    };

    // Track this pending message for WebSocket matching
    pendingMessagesRef.current.set(tempId, { mediaType: 'audio', sentAt: sentAtString });

    setMessages(prev => [...prev, optimisticMessage]);

    // Optimistic update - update contact list last message
    setContacts(prev => {
      return prev.map(c => {
        if (c.id === contactId) {
          return {
            ...c,
            lastMessage: '🎤 Áudio',
            lastMessageTime: sentAtString,
            updatedAt: sentAtString
          };
        }
        return c;
      }).sort((a, b) => {
        const timeA = a.lastMessageTime ? dayjs(a.lastMessageTime).valueOf() : 0;
        const timeB = b.lastMessageTime ? dayjs(b.lastMessageTime).valueOf() : 0;
        return timeB - timeA;
      });
    });

    try {
      const spark = isSparkContact(selectedContact);
      await sendAudioMessage(
        audioBlob,
        selectedContact.number,
        selectedContact.id,
        Number(selectedSectorId),
        currentUserId,
        false,
        sentAtString,
        spark
      );
      setMessages(prev => markOptimisticAsSent(prev, tempId));
      pendingMessagesRef.current.delete(tempId);
      // Não revogar o blob de imediato: a API pode demorar a devolver mediaUrl e o polling
      // substitui por URL http(s). Revogar cedo quebrava o player e mostrava só "🎤 Audio".
      window.setTimeout(() => {
        try {
          URL.revokeObjectURL(localAudioUrl);
        } catch {
          /* ignore */
        }
      }, 180_000);
    } catch (error) {
      console.error('Erro ao enviar áudio:', error);
      // Update message status to 'error'
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, status: 'error' as const, deliveryError: sendFailureText(error) } : m
      ));
    }
  };

  // Enviar mensagem
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedContact || !selectedSectorId) {
      return;
    }

    const tempId = Date.now();
    const contactId = selectedContact.id;
    let nowLocal = dayjs();

    if (messages.length > 0) {
      const maxSentAt = messages.reduce((max, m) => {
        const t = dayjs(m.sentAt).valueOf();
        return t > max ? t : max;
      }, 0);
      if (nowLocal.valueOf() <= maxSentAt) {
        nowLocal = dayjs(maxSentAt + 1000);
      }
    }

    const sentAtString = nowLocal.format();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Marcar que deve fazer scroll
    shouldScrollToBottomRef.current = true;
    const messageText = messageInput.trim();
    setMessageInput('');

    if (selectedContact) {
      updateHasNewMessages(prev => ({
        ...prev,
        [selectedContact.id]: false
      }));
    }

    const userId = currentUserId;
    if (userId === undefined) {
      console.error('Usuário não identificado');
      return;
    }

    // Optimistic update - add message immediately with 'sending' status
    const optimisticMessage: Message = {
      id: tempId,
      content: messageText,
      mediaType: 'text',
      mediaUrl: null,
      fileName: null,
      mimeType: null,
      sectorId: Number(selectedSectorId),
      contactID: contactId,
      sentAt: sentAtString,
      isSent: true,
      isRead: false,
      status: 'sending'
    };

    // Track this pending message for WebSocket matching
    pendingMessagesRef.current.set(tempId, { mediaType: 'text', sentAt: sentAtString });

    setMessages(prev => [...prev, optimisticMessage]);

    // Optimistic update - update contact list last message
    setContacts(prev => {
      return prev.map(c => {
        if (c.id === contactId) {
          return {
            ...c,
            lastMessage: messageText,
            lastMessageTime: sentAtString,
            updatedAt: sentAtString
          };
        }
        return c;
      }).sort((a, b) => {
        const timeA = a.lastMessageTime ? dayjs(a.lastMessageTime).valueOf() : 0;
        const timeB = b.lastMessageTime ? dayjs(b.lastMessageTime).valueOf() : 0;
        return timeB - timeA;
      });
    });

    try {
      await sendMessage({
        text: messageText,
        to: selectedContact.number,
        recipientPhone: selectedContact.number,
        contactId: contactId,
        sectorId: Number(selectedSectorId),
        userId: userId,
        isAnonymous: false,
        sentAt: sentAtString,
        timezone,
        isSparkContact: isSparkContact(selectedContact)
      });
      setMessages(prev => markOptimisticAsSent(prev, tempId));
      pendingMessagesRef.current.delete(tempId);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      // Update message status to 'error'
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, status: 'error' as const, deliveryError: sendFailureText(error) } : m
      ));
    }
  };

  // Manipular tecla Enter
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (messageInput.trim()) {
        handleSendMessage();
      }
    }
  };

  // Callback para limpar indicador de não lido (chamado pelo ContactItem antes de onSelect)
  const handleClearNewMessage = useCallback((contactId: number) => {
    updateHasNewMessages(prev => ({ ...prev, [contactId]: false }));
    if (setUnreadCount) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  }, [updateHasNewMessages, setUnreadCount]);

  // Selecionar contato
  const handleContactSelect = (contact: ContactData) => {
    // Marcar como lido no backend (apenas para contatos não-grupo)
    if (!contact.isGroup && selectedSectorId) {
      markContactAsViewed(Number(selectedSectorId), contact.id);
    }
    setSelectedContact(contact);
    setPage(0);
    setInitialLoad(true);
    setMessages([]); // Limpar mensagens anteriores
    shouldScrollToBottomRef.current = true; // Forçar scroll para baixo
    loadMessages(contact.id, 0, false, contact.isGroup);
  };

  // Context Menu Handlers removed (duplicates)

  // Renderizar mensagem
  const renderMessage = (message: Message) => {
    if (message.mediaType === 'internal_system') {
      return (
        <InternalSystemMessage
          key={message.id}
          id={message.id}
          content={message.content || ''}
          sentAt={message.sentAt}
          formatTime={formatMessageTime}
        />
      );
    }
    const isImage = message.mediaType === 'image';
    const isDocument = message.mediaType === 'document';
    const isAudio = message.mediaType === 'audio' || message.mediaType === 'voice';
    const isVideo = message.mediaType === 'video';
    const mediaUrl = coalesceMediaUrl(message as Message & Record<string, unknown>);
    const hasMediaUrl = !!mediaUrl;
    // Instagram: apagada pelo cliente não mostra o texto; editada ganha a marca.
    const metaView = metaMessageDisplay(message);
    const caption = metaView.deleted ? '' : getDisplayCaption(message.content);
    const hasContent = !!caption;

    return (
      <div key={message.id} className={`cweb-message ${message.isSent ? 'cweb-sent' : 'cweb-received'}`}>
        <div className="cweb-message-content">
          {message.channel && <div className="cweb-message-channel"><ChannelBadge channel={message.channel} label={message.accountLabel} /></div>}
          {/* Show sender name for received messages in groups */}
          {!message.isSent && selectedContact?.isGroup && message.senderName && (
            <div className="cweb-message-sender">{message.senderName}</div>
          )}
          {isImage && hasMediaUrl && (
            <a className="cweb-message-image" href={mediaUrl!} target="_blank" rel="noopener noreferrer">
              <img src={mediaUrl!} alt="Imagem" />
            </a>
          )}
          {isImage && !hasMediaUrl && (
            <div className="cweb-message-image-placeholder">
              <ImageIcon />
              <span>📷 Imagem</span>
            </div>
          )}
          {isDocument && hasMediaUrl && (
            <a className="cweb-message-document" href={mediaUrl!} target="_blank" rel="noopener noreferrer" download={message.fileName || undefined}>
              <DocumentIcon />
              <span>{message.fileName || message.content || 'Documento'}</span>
            </a>
          )}
          {isDocument && !hasMediaUrl && (
            <div className="cweb-message-document">
              <DocumentIcon />
              <span>{message.content || message.fileName || 'Documento'}</span>
            </div>
          )}
          {isAudio && hasMediaUrl && (
            <div className="cweb-message-audio">
              <AudioMessage
                src={mediaUrl!}
                isSent={message.isSent}
                onCancel={message.status === 'sending' ? () => {
                  setMessages(prev => prev.filter(m => m.id !== message.id));
                } : undefined}
              />
            </div>
          )}
          {isAudio && !hasMediaUrl && (
            <div className="cweb-message-audio-placeholder">
              <MicIcon />
              <span>🎤 Áudio</span>
            </div>
          )}
          {isVideo && hasMediaUrl && (
            <div className="cweb-message-video">
              <video controls>
                <source src={mediaUrl!} type={message.mimeType || 'video/mp4'} />
              </video>
            </div>
          )}
          {isVideo && !hasMediaUrl && (
            <div className="cweb-message-video-placeholder">
              <span>🎬 Vídeo</span>
            </div>
          )}
          {/* Show text content for text messages OR for media with captions */}
          {(!isImage && !isDocument && !isAudio && !isVideo && hasContent && !metaView.deleted) && (
            <span>{caption}</span>
          )}
          {metaView.deleted && <span className="cweb-message-deleted">{metaView.text}</span>}
          {metaView.edited && <span className="cweb-message-edited">editada</span>}
          {/* Show caption for media messages (skips placeholders like "[imagem]") */}
          {(isImage || isVideo) && hasContent && (
            <div className="cweb-message-caption">
              <span>{caption}</span>
            </div>
          )}
        </div>
        <WhatsAppDeliveryNote state={message.state} status={message.status} error={message.deliveryError} />
        <div className="cweb-message-info">
          <span className="cweb-message-time">
            {formatMessageTime(message.sentAt)}
          </span>
          {message.isSent && (
            <span className={`cweb-message-status ${message.status === 'sending' ? 'cweb-status-sending' : (message.status === 'error' ? 'cweb-status-error' : 'cweb-status-sent')}`}>
              {message.status === 'sending' && <span className="cweb-status-spinner"></span>}
              {(message.status === 'sent' || !message.status) && '✓'}
              {message.status === 'error' && '⚠️'}
            </span>
          )}
        </div>
      </div>
    );
  };

  const contactsDeduped = useMemo(() => dedupeChatContactsForList(contacts), [contacts]);

  // Filtrar + ORDENAR (memoizado): sem memo o filtro recomputa a cada render; a ordenação estável
  // (por recência, desempate por id) é a única fonte da verdade da ordem — mata o "piscar"/reorder.
  const filteredContacts = useMemo(() => contactsDeduped.filter(contact => {
    if (activeFilter !== 'whatsapp-groups' && contact.isGroup === true) {
      return false;
    }
    const name = contact.name ?? '';
    const number = contact.number ?? '';
    const useServerSearch = debouncedSearch.trim().length >= 2;
    const matchesSearch =
      useServerSearch ||
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      number.includes(searchTerm);
    const matchesUser = !selectedFilterUser || contact.assignedTo === selectedFilterUser;
    const matchesTag = !selectedFilterTag || contact.tagId === selectedFilterTag;
    const matchesStatus = selectedStatus === 'all' || contact.contactStatus === selectedStatus;

    // Apply activeFilter from Carousel
    let matchesActiveFilter = true;
    if (activeFilter === 'archived') {
      matchesActiveFilter = contact.archived === true;
    } else if (activeFilter === 'favorites') {
      matchesActiveFilter = contact.isFavorite === true && !contact.archived;
    } else if (activeFilter === 'unread') {
      const isViewed = contact.is_viewed ?? true;
      matchesActiveFilter = (!isViewed || hasNewMessages[contact.id] === true) && !contact.archived;
    } else if (isOperationalFilter(activeFilter)) {
      // Mesmas regras/janelas dos totais da Hoje (HJ-02).
      matchesActiveFilter = matchesOperationalFilter(
        activeFilter, contact, new Date(), hasNewMessages[contact.id] === true,
      );
    } else if (activeFilter === 'whatsapp-groups') {
      matchesActiveFilter = contact.isGroup === true;
    } else if (activeFilter.startsWith('group-')) {
      const groupId = parseInt(activeFilter.split('-')[1]);
      matchesActiveFilter = groupMembers[groupId]?.includes(contact.id) ?? false;
    } else {
      // 'all' filter - show non-archived contacts
      matchesActiveFilter = !contact.archived;
    }

    if (isLidOnlyAddress(contact.number)) {
      return false;
    }

    const matchesChannel = activeFilter === 'whatsapp-groups' || matchesChannelFilter(contact, channelFilter);

    return matchesSearch && matchesUser && matchesTag && matchesStatus && matchesActiveFilter && matchesChannel;
  }).sort(compareChatContactsByRecencyDesc), [contactsDeduped, activeFilter, debouncedSearch, searchTerm, selectedFilterUser, selectedFilterTag, selectedStatus, hasNewMessages, groupMembers, channelFilter]);

  // Função para transferir contato
  const handleTransferContact = async () => {
    if (!selectedContact || !selectedUserId) return;

    setIsTransferLoading(true);
    try {
      await updateContact(selectedContact.id, {
        name: selectedContact.name,
        tagId: selectedContact.tagId,
        phoneWhatsapp: selectedContact.number,
        avatarUrl: selectedContact.avatarUrl,
        email: selectedContact.email,
        notes: selectedContact.notes,
        sectorId: selectedContact.sectorId,
        isActive: selectedContact.isActive,
        priority: selectedContact.priority,
        aiActive: selectedContact.aiActive,
        assignedTo: selectedUserId
      });

      // Atualizar o contato na lista
      setContacts(prev => prev.map(contact =>
        contact.id === selectedContact.id
          ? { ...contact, assignedTo: selectedUserId }
          : contact
      ));

      setShowTransferModal(false);
      setSelectedUserId(null);
      setShowHeaderMenu(false);
    } catch (error) {
      console.error('Erro ao transferir contato:', error);
    } finally {
      setIsTransferLoading(false);
    }
  };

  // Função para alterar tag
  const handleUpdateTag = async () => {
    if (!selectedContact) return;

    setIsTagLoading(true);
    try {
      await updateContact(selectedContact.id, {
        name: selectedContact.name,
        tagId: selectedTagId,
        phoneWhatsapp: selectedContact.number,
        avatarUrl: selectedContact.avatarUrl,
        email: selectedContact.email,
        notes: selectedContact.notes,
        sectorId: selectedContact.sectorId,
        isActive: selectedContact.isActive,
        priority: selectedContact.priority,
        aiActive: selectedContact.aiActive,
        assignedTo: selectedContact.assignedTo
      });

      // Atualizar o contato na lista
      setContacts(prev => prev.map(contact =>
        contact.id === selectedContact.id
          ? { ...contact, tagId: selectedTagId }
          : contact
      ));

      setSelectedContact(prev => prev ? { ...prev, tagId: selectedTagId } : null);
      setShowTagModal(false);
      setSelectedTagId(null);
      setShowHeaderMenu(false);
    } catch (error) {
      console.error('Erro ao alterar tag:', error);
    } finally {
      setIsTagLoading(false);
    }
  };

  // Função para excluir contato
  const handleDeleteContact = async () => {
    if (!selectedContact) return;

    setIsDeletingContact(true);
    try {
      await deleteContact(selectedContact.id);

      // Remover o contato da lista
      setContacts(prev => prev.filter(contact => contact.id !== selectedContact.id));

      // Delete = deleted_at (não arquivar): só sai das listagens
      if (selectedContact.archived) {
        setArchivedContactsCount(prev => (prev > 0 ? prev - 1 : 0));
      } else {
        setTotalContactsCount(prev => (prev > 0 ? prev - 1 : 0));
      }
      if (selectedContact.isFavorite) {
        setFavoritesCount(prev => prev > 0 ? prev - 1 : 0);
      }

      // Remover notificações desse contato
      notificationService.removeByContactId(selectedContact.id);

      // Limpar seleção
      setSelectedContact(null);
      setMessages([]);
      setShowDeleteModal(false);
      setShowHeaderMenu(false);
    } catch (error) {
      console.error('Erro ao excluir contato:', error);
    } finally {
      setIsDeletingContact(false);
    }
  };

  // Função para arquivar/desarquivar contato
  const handleArchiveContact = async () => {
    if (!selectedContact) return;

    setIsArchivingContact(true);
    try {
      const isCurrentlyArchived = selectedContact.archived || false;
      await archiveContact(selectedContact.id, !isCurrentlyArchived);

      // Atualizar o status archived do contato na lista (client-side filtering cuida do resto)
      setContacts(prev => prev.map(c =>
        c.id === selectedContact.id ? { ...c, archived: !isCurrentlyArchived } : c
      ));

      // Atualizar a contagem de arquivados
      if (isCurrentlyArchived) {
        setArchivedContactsCount(prev => prev - 1);
        setTotalContactsCount(prev => prev + 1);
      } else {
        setArchivedContactsCount(prev => prev + 1);
        setTotalContactsCount(prev => prev - 1);
      }

      // Limpar seleção
      setSelectedContact(null);
      setMessages([]);
      setShowArchiveModal(false);
      setShowHeaderMenu(false);
    } catch (error) {
      console.error('Erro ao arquivar/desarquivar contato:', error);
    } finally {
      setIsArchivingContact(false);
    }
  };

  // Função para alternar entre AI e Humano
  const handleToggleAI = async () => {
    if (!selectedContact || !selectedSectorId || isTogglingAI) return;

    setIsTogglingAI(true);
    try {
      const token = SessionService.getToken();
      if (!token) return;

      const newAIStatus = selectedContact.aiActive !== 1;
      await toggleContactAI(Number(selectedSectorId), selectedContact.id, newAIStatus, token);

      // Atualizar contato local
      setContacts(prev =>
        prev.map(c =>
          c.id === selectedContact.id
            ? { ...c, aiActive: newAIStatus ? 1 : 0 }
            : c
        )
      );

      // Atualizar contato selecionado
      setSelectedContact(prev =>
        prev ? { ...prev, aiActive: newAIStatus ? 1 : 0 } : null
      );

      console.log(`Contact ${selectedContact.id} AI status changed to: ${newAIStatus ? 'AI' : 'Human'}`);
    } catch (error) {
      console.error('Erro ao alterar status AI:', error);
    } finally {
      setIsTogglingAI(false);
    }
  };
  // Função para criar novo contato
  const handleCreateContact = async () => {
    if (!newContactName.trim() || !newContactNumber.trim() || !selectedSectorId) {
      setNewContactError('Informe o nome e o número.');
      return;
    }

    setIsCreatingContact(true);
    setNewContactError('');

    try {
      const newContact = await createContact({
        name: newContactName.trim(),
        phoneNumber: newContactNumber.trim(),
        email: '',
        sectorId: Number(selectedSectorId)
      });

      if (newContact) {
        const toRow = (c: ApiContact): ContactData => ({
          id: c.id,
          name: c.name,
          number: c.number,
          avatarUrl: c.avatarUrl,
          email: c.email,
          notes: c.notes,
          tagId: c.tagId,
          sectorId: c.sectorId,
          isActive: c.isActive,
          priority: c.priority,
          contactStatus: c.contactStatus,
          aiActive: c.aiActive,
          aiAgentName: c.aiAgentName ?? null,
          assignedTo: c.assignedTo,
          createdAt: c.createdAt,
          is_viewed: c.is_viewed ?? c.isViewed,
          archived: c.archived,
          isGroup: false,
          isFavorite: false,
        });
        const row = toRow(newContact);
        setContacts(prev => {
          const rest = prev.filter(x => x.id !== row.id);
          return [row, ...rest];
        });
        setNewContactName('');
        setNewContactNumber('');
        setNewContactError('');
        setShowNewContactModal(false);
        setSearchTerm('');
        setActiveFilter('all');
        setSelectedFilterUser(null);
        setSelectedFilterTag(null);
        setSelectedStatus('all');
        setSelectedContact(row);
      } else {
        setNewContactError('Não foi possível criar o contato. Tente de novo.');
      }
    } catch (error: unknown) {
      if (error instanceof DuplicateContactError) {
        const c = error.existingContact;
        const row: ContactData = {
          id: c.id,
          name: c.name,
          number: c.number,
          avatarUrl: c.avatarUrl,
          email: c.email,
          notes: c.notes,
          tagId: c.tagId,
          sectorId: c.sectorId,
          isActive: c.isActive,
          priority: c.priority,
          contactStatus: c.contactStatus,
          aiActive: c.aiActive,
          aiAgentName: c.aiAgentName ?? null,
          assignedTo: c.assignedTo,
          createdAt: c.createdAt,
          is_viewed: c.is_viewed ?? c.isViewed,
          archived: c.archived,
          isGroup: false,
          isFavorite: false,
        };
        setContacts(prev => {
          const rest = prev.filter(x => x.id !== row.id);
          return [row, ...rest];
        });
        setNewContactName('');
        setNewContactNumber('');
        setNewContactError('');
        setShowNewContactModal(false);
        setSearchTerm('');
        setActiveFilter('all');
        setSelectedFilterUser(null);
        setSelectedFilterTag(null);
        setSelectedStatus('all');
        setSelectedContact(row);
        return;
      }
      console.error('Erro ao criar contato:', error);
      setNewContactError(apiErrorMessage(error, 'Não foi possível criar o contato. Tente de novo.'));
    } finally {
      setIsCreatingContact(false);
    }
  };

  // Função para verificar se pode excluir contato
  const canDeleteContact = () => {
    if (!selectedContact) return false;

    // Admin pode excluir qualquer contato
    if (isCurrentUserAdmin) return true;

    // Usuário comum só pode excluir se for o responsável
    return selectedContact.assignedTo === currentUserId;
  };

  // Função para verificar se pode transferir contato
  const canTransferContact = () => {
    if (!selectedContact) return false;

    // Admin pode transferir qualquer contato
    if (isCurrentUserAdmin) return true;

    // Usuário comum só pode transferir se for o responsável
    return selectedContact.assignedTo === currentUserId;
  };

  const mergePeopleWithWhatsAppGroups = useCallback((peopleRows: ContactData[], sectorNum: number): ContactData[] => {
    const isGroupsTab = activeFilter === 'whatsapp-groups';
    const baseRows = isGroupsTab ? peopleRows : peopleRows.filter(c => !c.isGroup);
    const deduped = dedupeChatContactsForList(baseRows);
    if (!isGroupsTab) {
      return deduped;
    }
    const groups = whatsappGroupsRef.current;
    if (!groups?.length) return deduped;

    const existingGroupKeys = new Set(
      deduped.filter(c => c.isGroup).map(c => normalizeGroupJidKey(c.number))
    );

    const waGroupContacts: OriginalContactData[] = groups
      .filter(g => !existingGroupKeys.has(normalizeGroupJidKey(g.jid)))
      .map((g) => ({
        id: g.contactId ?? g.id,
        name: g.name,
        number: g.jid,
        avatarUrl: g.avatarUrl ?? null,
        email: '',
        notes: '',
        tagId: null,
        sectorId: sectorNum,
        isActive: true,
        isGroup: true,
        contactStatus: 'active',
        priority: 'medium',
        aiActive: 0,
        assignedTo: null,
        createdAt: new Date().toISOString(),
        is_viewed: true,
        lastMessage: g.lastMessage,
        lastMessageTime: g.lastMessageTime,
      }));

    return dedupeChatContactsForList([...deduped, ...waGroupContacts]);
  }, [activeFilter]);

  const loadMoreChatContacts = useCallback(async () => {
    if (!selectedSectorId || chatLoadLockRef.current || !chatHasMore) return;
    const sectorId = SessionService.getSectorId();
    if (!sectorId) return;
    chatLoadLockRef.current = true;
    setChatLoadingMore(true);
    try {
      const sectorNum = Number(sectorId);
      const onlyArchived = activeFilter === 'archived';
      const searchQ = debouncedSearch.length >= 2 ? debouncedSearch : undefined;
      const offset = chatNextOffsetRef.current;
      const listView = activeFilter === 'whatsapp-groups' ? 'groups' : 'chat';
      const res = await getContactsWithFilters(
        sectorNum,
        false,
        onlyArchived,
        CHAT_CONTACTS_PAGE_SIZE,
        offset,
        listView,
        searchQ,
        listView === 'chat' ? channelFilter : undefined
      );
      const raw = Array.isArray((res as any)?.data) ? (res as any).data : [];
      const chunk: ContactData[] = [];
      for (const row of raw) {
        const c = mapApiContactToContact(row as Record<string, unknown>);
        if (c) chunk.push(mapApiToContactData(c));
      }
      setContacts(prev => {
        const basePrev = activeFilter === 'whatsapp-groups' ? prev : prev.filter(c => !c.isGroup);
        const people = dedupeChatContactsForList([...basePrev, ...chunk]);
        return mergePeopleWithWhatsAppGroups(people, sectorNum);
      });
      const pag = (res as any).pagination;
      chatNextOffsetRef.current = pag?.nextOffset ?? offset + chunk.length;
      setChatHasMore(Boolean(pag?.hasMore));
    } catch (e) {
      console.error('loadMoreChatContacts', e);
    } finally {
      chatLoadLockRef.current = false;
      setChatLoadingMore(false);
    }
  }, [selectedSectorId, chatHasMore, activeFilter, debouncedSearch, mergePeopleWithWhatsAppGroups, channelFilter]);

  // Listen for sector changes handled by Context now
  useEffect(() => {
    if (!selectedSectorId) {
      setContacts([]);
      setMessages([]);
      setSelectedContact(null);
      setUsers([]);
      setTags([]);
      setInternalGroups([]);
      setWhatsappGroups([]);
      setTotalContactsCount(0);
      setArchivedContactsCount(0);
      setFavoritesCount(0);
    }
  }, [selectedSectorId]);

  // Lista de chat: páginas via API (sem carregar tudo); busca no servidor com debounce (2+ chars).
  useEffect(() => {
    const sectorId = SessionService.getSectorId();
    if (!sectorId || !selectedSectorId) return;

    let cancelled = false;
    chatNextOffsetRef.current = 0;
    chatLoadLockRef.current = true;
    setChatLoadingMore(true);

    const run = async () => {
      try {
        const sectorNum = Number(sectorId);
        const onlyArchived = activeFilter === 'archived';
        const searchQ = debouncedSearch.length >= 2 ? debouncedSearch : undefined;
        const listView = activeFilter === 'whatsapp-groups' ? 'groups' : 'chat';
        const res = await getContactsWithFilters(
          sectorNum,
          false,
          onlyArchived,
          CHAT_CONTACTS_PAGE_SIZE,
          0,
          listView,
          searchQ,
          listView === 'chat' ? channelFilter : undefined
        );
        if (cancelled) return;
        const raw = Array.isArray((res as any)?.data) ? (res as any).data : [];
        const chunk: ContactData[] = [];
        for (const row of raw) {
          const c = mapApiContactToContact(row as Record<string, unknown>);
          if (c) chunk.push(mapApiToContactData(c));
        }
        const merged = mergePeopleWithWhatsAppGroups(chunk, sectorNum);
        setContacts(merged);
        const pag = (res as any).pagination;
        chatNextOffsetRef.current = pag?.nextOffset ?? chunk.length;
        setChatHasMore(Boolean(pag?.hasMore));
      } catch (error) {
        console.error('Erro ao recarregar contatos:', error);
      } finally {
        if (!cancelled) {
          chatLoadLockRef.current = false;
          setChatLoadingMore(false);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [selectedSectorId, activeFilter, debouncedSearch, mergePeopleWithWhatsAppGroups, channelFilter]);

  // Na aba Grupos, quando os grupos WA chegam/atualizam (poll), incluir linhas sintéticas sem esperar outro fetch da lista.
  useEffect(() => {
    if (activeFilter !== 'whatsapp-groups' || !selectedSectorId) return;
    const sectorNum = Number(selectedSectorId);
    setContacts(prev => mergePeopleWithWhatsAppGroups(prev, sectorNum));
  }, [whatsappGroups, activeFilter, selectedSectorId, mergePeopleWithWhatsAppGroups]);

  // ── Polling fallback: refresh messages every 3 seconds when a contact is open ──
  useEffect(() => {
    if (!selectedContact || !selectedSectorId) return;

    const contactId = selectedContact.id;

    // Merge fresh messages with pending "sending" ones, only update state if something changed
    const mergeAndUpdate = (freshMessages: Message[]) => {
      setMessages(prev => {
        const enrichedFresh = freshMessages.map(fm => enrichAudioMessageWithPendingBlob(fm, prev));
        const sendingMsgs = prev.filter(isOptimisticOutgoing);
        const keptSending = sendingMsgs.filter(
          opt => !enrichedFresh.some(fm => matchesPendingOptimisticContent(opt, fm))
        );
        const merged = [...enrichedFresh, ...keptSending]
          .sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf());

        // Skip update if nothing changed (same length, same IDs in same order, same content)
        if (merged.length === prev.length) {
          const unchanged = prev.every((m, i) =>
            merged[i]?.id === m.id &&
            merged[i]?.content === m.content &&
            merged[i]?.status === m.status &&
            merged[i]?.mediaUrl === m.mediaUrl &&
            merged[i]?.state === m.state &&
            merged[i]?.deliveryError === m.deliveryError &&
            merged[i]?.accountLabel === m.accountLabel
          );
          if (unchanged) return prev;
        }

        return merged;
      });
    };

    const pollMessages = async () => {
      try {
        const result = await getMessagesByContactIdWithPagination(contactId, 50, 0);
        const freshMessages: Message[] = (result.messages || []).map((m: any) => ({
          id: m.id,
          content: m.content || null,
          mediaType: m.mediaType || 'text',
          mediaUrl: coalesceMediaUrl(m),
          fileName: m.fileName || null,
          mimeType: m.mimeType || null,
          sectorId: m.sectorId || Number(selectedSectorId),
          contactID: m.contactID || contactId,
          sentAt: m.sentAt || new Date().toISOString(),
          isSent: m.isSent ?? false,
          isRead: m.isRead ?? false,
          senderName: m.senderName || undefined,
          channel: m.channel || undefined,
          accountLabel: m.accountLabel || undefined,
          state: m.state || undefined,
          deliveryError: m.deliveryError || undefined,
          status: 'sent' as const
        }));
        mergeAndUpdate(freshMessages);
      } catch (err) {
        // Silent - will retry on next poll
      }
    };

    const intervalId = setInterval(pollMessages, 3000);
    void pollMessages();
    return () => clearInterval(intervalId);
  }, [selectedContact?.id, selectedSectorId]);

  // Open contact via sessionStorage + custom event (works with CombinedMenu layout)
  const [pendingContactId, setPendingContactId] = useState<number | null>(() => {
    const stored = sessionStorage.getItem('pendingContactId');
    if (stored) {
      sessionStorage.removeItem('pendingContactId');
      return parseInt(stored, 10);
    }
    return null;
  });

  // /chat?contactId=&draft=&filter= — abre contato, rascunho e/ou filtro da operação (HJ-02)
  useEffect(() => {
    const link = readChatLink(location.search);
    if (link.contactId == null && !link.filter) return;
    if (link.filter) {
      skipDeselectOnFilterRef.current = true;
      setActiveFilter(link.filter);
    }
    if (link.contactId != null) {
      pendingDraftRef.current = link.draft;
      setPendingContactId(link.contactId);
    }
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!selectedContact || selectedContact.isGroup) return;
    rememberActiveContact(selectedContact);
  }, [selectedContact]);

  // navigate('/chat', { state: { selectedContactId } }) — ex.: ContactsPage "Open Chat"
  const selectedContactIdFromState = (location.state as { selectedContactId?: number } | null | undefined)?.selectedContactId;
  useEffect(() => {
    if (selectedContactIdFromState == null) return;
    const id =
      typeof selectedContactIdFromState === 'number'
        ? selectedContactIdFromState
        : parseInt(String(selectedContactIdFromState), 10);
    if (!Number.isFinite(id)) return;
    pendingDraftRef.current = null;
    setPendingContactId(id);
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
  }, [selectedContactIdFromState, location.pathname, location.search, navigate]);

  // Listen for openContactInChat event
  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent).detail?.contactId;
      if (id) {
        pendingDraftRef.current = null;
        setPendingContactId(id);
      }
    };
    window.addEventListener('openContactInChat', handler);
    return () => window.removeEventListener('openContactInChat', handler);
  }, []);

  // Resolve pending contact - search local lists first, then fetch from API
  useEffect(() => {
    if (!pendingContactId || !selectedSectorId) return;

    const openContact = (contact: ContactData) => {
      if (contact.sectorId !== Number(selectedSectorId)) {
        setPendingContactId(null);
        pendingDraftRef.current = null;
        return;
      }
      skipDeselectOnFilterRef.current = true;
      if (contact.archived) setActiveFilter('archived');
      else if (contact.isFavorite) setActiveFilter('favorites');
      else setActiveFilter('all');
      setContacts(prev => (prev.some(c => c.id === contact.id) ? prev : [...prev, contact]));

      setPendingContactId(null);
      if (!contact.isGroup && selectedSectorId) {
        markContactAsViewed(Number(selectedSectorId), contact.id);
      }
      setSelectedContact(contact);
      if (pendingDraftRef.current) {
        setMessageInput(pendingDraftRef.current);
        pendingDraftRef.current = null;
      }
      setPage(0);
      setInitialLoad(true);
      setMessages([]);
      shouldScrollToBottomRef.current = true;
      loadMessages(contact.id, 0, false, contact.isGroup);
    };

    // Try local + external contacts first
    const allContacts = [...contacts, ...(externalContacts || [])];
    const localContact = allContacts.find(c => c.id === pendingContactId);
    if (localContact) {
      openContact(localContact);
      return;
    }

    // Not found locally - fetch from API (contact list is paginated)
    const fetchContact = async () => {
      try {
        const result = await getContactById(pendingContactId);
        if (!result) {
          setPendingContactId(null);
          pendingDraftRef.current = null;
          return;
        }
        // C# backend returns { Data } (capital D), Go returns { data }
        const raw = (result as any).Data || (result as any).data || result;
        // Map field names: C# uses Id/Name/IsGroup, normalize to match ContactData
        const contact: ContactData = {
          ...raw,
          id: raw.Id ?? raw.id,
          name: raw.Name ?? raw.name,
          number: raw.Number ?? raw.number,
          avatarUrl: raw.AvatarUrl ?? raw.avatarUrl ?? raw.avatarURL ?? null,
          email: raw.Email ?? raw.email ?? '',
          notes: raw.Notes ?? raw.notes ?? null,
          tagId: raw.TagId ?? raw.tagId ?? null,
          sectorId: raw.SectorId ?? raw.sectorId,
          isActive: raw.IsActive ?? raw.isActive ?? true,
          priority: raw.Priority ?? raw.priority ?? 'medium',
          contactStatus: raw.ContactStatus ?? raw.contactStatus ?? 'active',
          aiActive: raw.AiActive ?? raw.aiActive ?? raw.ai_active ?? 0,
          assignedTo: raw.AssignedTo ?? raw.assignedTo ?? null,
          createdAt: raw.CreatedAt ?? raw.createdAt,
          isGroup: raw.IsGroup ?? raw.isGroup ?? false,
          isFavorite: raw.IsFavorite ?? raw.isFavorite ?? false,
          archived: raw.Archived ?? raw.archived ?? false,
          is_viewed: raw.IsViewed ?? raw.is_viewed ?? true,
        };
        if (contact.id) {
          openContact(contact);
        } else {
          setPendingContactId(null);
          pendingDraftRef.current = null;
        }
      } catch (err) {
        console.error('[ChatWeb] Error fetching contact by ID:', err);
        setPendingContactId(null);
        pendingDraftRef.current = null;
      }
    };
    fetchContact();
  }, [pendingContactId, contacts, externalContacts, selectedSectorId, loadMessages]);

  // Fetch Internal Groups and WhatsApp Groups on sector change
  useEffect(() => {
    if (selectedSectorId) {
      // Fetch Internal Groups
      getInternalGroups(Number(selectedSectorId))
        .then(res => {
          const groups = res.data || [];
          setInternalGroups(groups);
          // Carregar membros de cada grupo para mostrar contagem
          groups.forEach((g: InternalGroup) => {
            getContactsInGroup(g.id).then(res => {
              const memberIds = (res.data || []).map((c: any) => c.id);
              setGroupMembers(prev => ({ ...prev, [g.id]: memberIds }));
            }).catch(err => console.error(`Error loading members for group ${g.id}`, err));
          });
        })
        .catch(err => console.error('Error fetching internal groups:', err));

      // Fetch WhatsApp Groups (synchronized from WhatsApp)
      getWhatsAppGroups(Number(selectedSectorId))
        .then(res => {
          setWhatsappGroups(res.data || []);
          console.log('[ChatWeb] WhatsApp groups loaded:', res.data?.length || 0);
        })
        .catch(err => console.error('Error fetching whatsapp groups:', err));
    }
  }, [selectedSectorId]);

  // Polling de grupos: atualizar lista quando novos grupos forem sincronizados (ex.: mensagem de grupo novo)
  const GROUPS_POLL_INTERVAL = 6000; // 6 segundos
  useEffect(() => {
    if (!selectedSectorId) return;
    const pollGroups = () => {
      getWhatsAppGroups(Number(selectedSectorId))
        .then(res => {
          setWhatsappGroups(res.data || []);
        })
        .catch(() => {});
    };
    const intervalId = setInterval(pollGroups, GROUPS_POLL_INTERVAL);
    return () => clearInterval(intervalId);
  }, [selectedSectorId]);

  // Handle Context Menu
  const handleArchive = async (contact: any) => {
    try {
      const newArchivedState = !contact.archived;
      await archiveContact(contact.id, newArchivedState);
      setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, archived: newArchivedState } : c));

      // Update counts
      if (newArchivedState) {
        setTotalContactsCount(prev => prev - 1);
        setArchivedContactsCount(prev => prev + 1);
        if (contact.isFavorite) setFavoritesCount(prev => prev > 0 ? prev - 1 : 0);
      } else {
        setTotalContactsCount(prev => prev + 1);
        setArchivedContactsCount(prev => prev > 0 ? prev - 1 : 0);
        if (contact.isFavorite) setFavoritesCount(prev => prev + 1);
      }

      if (selectedContact?.id === contact.id) {
        setSelectedContact(prev => prev ? { ...prev, archived: newArchivedState } : null);
      }
    } catch (error) {
      console.error('Error archiving contact:', error);
    }
  };

  const toggleGroupMembership = async (groupId: number) => {
    if (!selectedContactForGroup) return;

    const isMember = contactGroups.includes(groupId);
    try {
      if (isMember) {
        await removeContactFromGroup(groupId, selectedContactForGroup.id);
        setContactGroups(prev => prev.filter(id => id !== groupId));
        // Update cache if exists
        if (groupMembers[groupId]) {
          setGroupMembers(prev => ({ ...prev, [groupId]: prev[groupId].filter(id => id !== selectedContactForGroup.id) }));
        }
      } else {
        await addContactToGroup(groupId, selectedContactForGroup.id);
        setContactGroups(prev => [...prev, groupId]);
        // Update cache if exists
        if (groupMembers[groupId]) {
          setGroupMembers(prev => ({ ...prev, [groupId]: [...prev[groupId], selectedContactForGroup.id] }));
        }
      }
    } catch (error) {
      console.error('Error toggling group membership:', error);
    }
  };

  // Handlers for Context Menu
  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, contactId: null });
  };

  const handleFavoriteFromMenu = async () => {
    if (!contextMenu.contactId) return;

    const contact = contacts.find(c => c.id === contextMenu.contactId);
    if (!contact) return;

    const newFavoriteState = !contact.isFavorite;

    try {
      await toggleFavorite(contextMenu.contactId, newFavoriteState);

      // Update local state
      setContacts(prev => prev.map(c =>
        c.id === contextMenu.contactId
          ? { ...c, isFavorite: newFavoriteState }
          : c
      ));

      // Update count
      setFavoritesCount(prev => newFavoriteState ? prev + 1 : (prev > 0 ? prev - 1 : 0));

    } catch (error) {
      console.error('Error toggling favorite:', error);
    }

    closeContextMenu();
  };

  const handleArchiveFromMenu = async () => {
    if (!contextMenu.contactId) return;
    const contact = contacts.find(c => c.id === contextMenu.contactId);
    if (!contact) return;

    await handleArchive(contact);
    closeContextMenu();
  };

  const handleOpenGroupModal = () => {
    if (!contextMenu.contactId) return;
    const contact = contacts.find(c => c.id === contextMenu.contactId);
    if (!contact) return;

    setSelectedContactForGroup(contact);
    // Load groups for this contact
    getGroupsForContact(contact.id).then(res => {
      const groupIds = (res.data || []).map((g: any) => g.id);
      setContactGroups(groupIds);
      setShowGroupModal(true);
    }).catch(err => console.error("Error loading contact groups", err));

    closeContextMenu();
  };

  const handleContextMenu = (e: React.MouseEvent, contactOrId: any) => {
    e.preventDefault();
    const contactId = typeof contactOrId === 'object' ? contactOrId.id : contactOrId;
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      contactId
    });
  };

  // Disponibilidade do mic: NÃO abrir getUserMedia no mount (isso deixa o mic "em uso").
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const perms = navigator.permissions;
        if (perms?.query) {
          const status = await perms.query({ name: 'microphone' as PermissionName });
          if (!cancelled) {
            setIsMicrophoneAvailable(status.state !== 'denied');
            setIsCheckingMicrophone(false);
          }
          return;
        }
      } catch {
        /* Safari / browsers sem Permissions API */
      }
      if (!cancelled) {
        // Desconhecido: habilita o botão; o mic só abre quando o usuário gravar de fato.
        setIsMicrophoneAvailable(true);
        setIsCheckingMicrophone(false);
      }
    };
    void probe();
    return () => { cancelled = true; };
  }, []);

  // Efeito para scroll automático quando mensagens mudam
  useEffect(() => {
    if (shouldScrollToBottomRef.current) {
      smartScrollToBottom();
      shouldScrollToBottomRef.current = false;
    }
  }, [messages, smartScrollToBottom]);

  // Efeito para scroll inicial quando carrega mensagens
  useEffect(() => {
    if (!isLoadingMore && !isMessagesLoading && messages.length > 0 && initialLoad) {
      // Aguardar um pouco para garantir que o DOM foi renderizado
      setTimeout(() => {
        smartScrollToBottom(true);
        setInitialLoad(false); // Marcar que o carregamento inicial foi concluído
      }, 300); // Aumentar o tempo para garantir que tudo foi renderizado
    }
  }, [messages.length, isLoadingMore, isMessagesLoading, initialLoad, smartScrollToBottom]);

  // Efeito adicional para garantir scroll quando contato é selecionado
  useEffect(() => {
    if (selectedContact && messages.length > 0 && !isMessagesLoading) {
      // Aguardar um pouco mais para garantir que tudo foi renderizado
      setTimeout(() => {
        scrollToBottom('instant');
      }, 100);
    }
  }, [selectedContact?.id, messages.length, isMessagesLoading, scrollToBottom]);

  // Monitorar scroll do usuário
  useEffect(() => {
    const handleScroll = () => {
      const container = messagesContainerRef.current;
      if (!container) return;

      const currentScrollTop = container.scrollTop;
      const scrollDifference = Math.abs(currentScrollTop - lastScrollTopRef.current);

      // Se o usuário fez scroll manual (diferença significativa)
      if (scrollDifference > 10) {
        isUserScrollingRef.current = true;

        // Reset após um tempo
        setTimeout(() => {
          isUserScrollingRef.current = false;
        }, 1000);
      }

      lastScrollTopRef.current = currentScrollTop;

      // Mostrar/ocultar botão de scroll para baixo
      const threshold = 120;
      const isNearBottom = currentScrollTop >= (container.scrollHeight - container.clientHeight - threshold);
      setShowScrollToBottom(!isNearBottom);
    };

    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div className={`chat-web-wrapper ${visionEnabled ? 'mai-inbox-light' : ''}`}>
      <div className="cweb-container">
        {/* WebSocketStatus removed - using polling */}
        {/* Sidebar de contatos */}
        <div className="cweb-sidebar">
          <div className="cweb-sidebar-header">
            <button
              className="cweb-new-contact-btn"
              onClick={() => selectedSectorId && setShowNewContactModal(true)}
              disabled={!selectedSectorId}
            >
              {visionEnabled ? '+ Nova conversa' : '+ Novo contato'}
            </button>
            <button
              className="cweb-filters-btn"
              onClick={() => selectedSectorId && setShowFiltersModal(true)}
              disabled={!selectedSectorId}
            >
              Filtros
            </button>
          </div>

          <div className="cweb-search">
            <input
              type="text"
              placeholder={selectedSectorId
                ? (visionEnabled ? 'Buscar conversas, clientes…' : 'Buscar contatos…')
                : 'Selecione uma conexão primeiro'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!selectedSectorId}
            />
          </div>
          {selectedSectorId && activeFilter !== 'whatsapp-groups' && (
            <div className="cweb-channel-filter-row">
              <ChannelFilterSelect value={channelFilter} onChange={setChannelFilter} options={channelFilterOptions({ instagram: instagramActive, messenger: messengerActive, webchat: webchatActive })} />
            </div>
          )}

          {selectedSectorId && (
            <div className="cweb-carousel-section">
              <Carousel
                variant="grid"
                items={[
                  { id: 'all', name: 'Todas', type: 'contact' as const },
                  { id: 'unread', name: 'Não lidas', type: 'contact' as const },
                  ...(visionEnabled ? [
                    { id: 'ai-attending', name: 'IA atendendo', type: 'contact' as const },
                    { id: 'needs-human', name: 'Humanas', type: 'contact' as const },
                    { id: 'awaiting', name: 'Aguardando', type: 'contact' as const },
                    { id: 'risk-silent', name: 'Risco / silêncio', type: 'contact' as const },
                  ] : []),
                  { id: 'archived', name: 'Arquivadas', type: 'archived' as const },
                  { id: 'favorites', name: 'Favoritas', type: 'favorite' as const },
                  { id: 'whatsapp-groups', name: 'Grupos', type: 'group' as const },
                  ...(internalGroups || []).map(g => ({ id: `group-${g.id}`, name: g.name, type: 'group' as const }))
                ]}

                selectedId={activeFilter}
                onSelect={(item) => {
                  if (item.id === 'all') setActiveFilter('all');
                  else if (item.id === 'unread') setActiveFilter('unread');
                  else if (item.id === 'ai-attending') setActiveFilter('ai-attending');
                  else if (item.id === 'needs-human') setActiveFilter('needs-human');
                  else if (item.id === 'awaiting') setActiveFilter('awaiting');
                  else if (item.id === 'risk-silent') setActiveFilter('risk-silent');
                  else if (item.id === 'archived') setActiveFilter('archived');
                  else if (item.id === 'favorites') setActiveFilter('favorites');
                  else if (item.id === 'whatsapp-groups') setActiveFilter('whatsapp-groups');
                  else if (String(item.id).startsWith('group-')) {
                    const groupId = parseInt(String(item.id).split('-')[1]);
                    setActiveFilter(String(item.id));
                    // Always fetch fresh members when clicking group
                    getContactsInGroup(groupId).then(res => {
                      const memberIds = (res.data || []).map((c: any) => c.id);
                      setGroupMembers(prev => ({ ...prev, [groupId]: memberIds }));
                    }).catch(err => {
                      console.error('Error fetching group members:', err);
                      setGroupMembers(prev => ({ ...prev, [groupId]: [] }));
                    });
                  }
                }}
              />
            </div>
          )}


          <div className="cweb-contacts">
            {!selectedSectorId ? (
              <div className="cweb-no-sector">
                <FiMenu size={48} style={{ opacity: 0.4, marginBottom: 12 }} />
                <span>Selecione uma conexão no menu principal para ver os contatos</span>
              </div>
            ) : (
              <Virtuoso
                style={{ height: '100%', minHeight: 0, flex: 1 }}
                data={filteredContacts}
                computeItemKey={(_index, contact) => contact.id}
                endReached={() => {
                  if (chatHasMore && !chatLoadingMore) void loadMoreChatContacts();
                }}
                components={{
                  EmptyPlaceholder: () =>
                    chatLoadingMore ? null : (
                      <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                        {channelFilter === 'email'
                          ? 'Nenhuma conversa por e-mail nesta conexão.'
                          : channelFilter === 'instagram'
                            ? 'Nenhuma conversa pelo Instagram nesta conexão.'
                          : channelFilter === 'messenger'
                            ? 'Nenhuma conversa pelo Messenger nesta conexão.'
                          : channelFilter === 'webchat'
                            ? 'Nenhuma conversa pelo Site neste setor.'
                          : debouncedSearch.length >= 2
                            ? 'Nenhuma conversa encontrada para essa busca.'
                            : 'Nenhuma conversa neste filtro.'}
                      </div>
                    ),
                  Footer: () =>
                    chatLoadingMore ? (
                      <div style={{ padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                        <ImSpinner2 style={{ animation: 'spin 1s linear infinite' }} /> Carregando mais contatos…
                      </div>
                    ) : null,
                }}
                itemContent={(_index, contact) => (
                  <ContactItem
                    key={contact.id}
                    contact={{
                      id: contact.id,
                      name: contact.name,
                      number: contact.number,
                      email: contact.email,
                      avatarUrl: contact.avatarUrl,
                      lastMessage: contact.lastMessage,
                      lastMessageTime: contact.lastMessageTime,
                      tagId: contact.tagId,
                      tagColor: tags.find(t => t.id === contact.tagId)?.color,
                      isGroup: contact.isGroup,
                      isFavorite: contact.isFavorite,
                      contactStatus: contact.contactStatus,
                      aiActive: contact.aiActive,
                      aiAgentName: contact.aiAgentName,
                      unreadCount: contact.unreadCount,
                      lastMessageChannel: contact.lastMessageChannel,
                    }}
                    isSelected={selectedContact?.id === contact.id}
                    onSelect={() => handleContactSelect(contact)}
                    hasNewMessage={hasNewMessages?.[contact.id] || false}
                    onClearNewMessage={handleClearNewMessage}
                    onContextMenu={handleContextMenu}
                  />
                )}
              />
            )}
          </div>
        </div>

        {/* Área principal do chat */}
        <div className="cweb-main">
          {selectedContact ? (
            <>
              {/* Header do chat */}
              <div className="cweb-header">
              <div className="cweb-header-contact">
                <div className="cweb-header-avatar">
                  <img
                    src={selectedContact.avatarUrl || '/user.png'}
                    alt={getContactPickerPrimaryLabel(selectedContact.name, selectedContact.number)}
                    onError={(e) => {
                      const el = e.target as HTMLImageElement;
                      el.style.display = 'none';
                      const fallback = el.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div className="cweb-avatar-initials" style={{ display: 'none' }}>
                    {getContactPickerPrimaryLabel(selectedContact.name, selectedContact.number)
                        .split(' ')
                        .map(n => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)}
                  </div>
                </div>
                <div className="cweb-header-info">
                  <div className="cweb-header-name">
                      {getContactPickerPrimaryLabel(selectedContact.name, selectedContact.number)}
                      {!selectedContact.isGroup && (
                        <button onClick={() => setIsEditingName(true)} className="cweb-edit-name-btn">
                          <FiEdit2 size={16} />
                        </button>
                      )}
                    </div>
                    {(() => {
                      const sparkOrigin = formatSparkOriginLabel(selectedContact);
                      return sparkOrigin ? (
                        <div className="cweb-header-phone" title="WhatsApp oficial de entrada">
                          {sparkOrigin}
                        </div>
                      ) : null;
                    })()}
                    {visionEnabled && !selectedContact.isGroup && (
                      <div style={{ marginTop: 4 }}>
                        <ConversationStatusBadge
                          status={resolveConversationStatus(
                            selectedContact.aiActive,
                            selectedContact.unreadCount,
                            hasNewMessages?.[selectedContact.id]
                          )}
                          agentName={selectedContact.aiAgentName}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="cweb-header-actions">
                  {visionEnabled && !selectedContact.isGroup && (
                    <button
                      type="button"
                      className="cweb-header-button"
                      title="Registrar"
                      onClick={() => openRegisterConversation({
                        contactId: selectedContact.id,
                        contactName: getContactPickerPrimaryLabel(selectedContact.name, selectedContact.number),
                        conversationId: selectedContact.id,
                        sourceSurface: 'inbox',
                        screen: 'chat',
                        pathname: '/chat',
                        subjectKind: 'qualified_contact',
                      })}
                      style={{ fontSize: 12, padding: '4px 8px', width: 'auto' }}
                    >
                      <FiMic size={14} />
                      Registrar
                    </button>
                  )}
                  {visionEnabled && !selectedContact.isGroup && (
                    <button
                      type="button"
                      className="cweb-header-button"
                      title="Memória do cliente"
                      onClick={() => setShowMemoryPanel((v) => !v)}
                      style={{ fontSize: 12, padding: '4px 8px', width: 'auto' }}
                    >
                      Memória
                    </button>
                  )}
                  {/* AI Toggle Switch */}
                  {!selectedContact.isGroup && (
                    <div
                      className="cweb-ai-toggle"
                      title={selectedContact.aiActive === 1
                        ? (selectedContact.aiAgentName ? `${selectedContact.aiAgentName} (IA) está atendendo. Clique para assumir.` : 'A IA está atendendo. Clique para assumir.')
                        : 'Você está atendendo. Clique para devolver à IA.'}
                    >
                      <span className={`cweb-ai-label ${selectedContact.aiActive === 1 ? 'active' : ''}`}>
                        <FiCpu size={14} />
                        {selectedContact.aiActive === 1
                          ? (visionEnabled && selectedContact.aiAgentName ? `IA · ${selectedContact.aiAgentName.split(/\s+/)[0]}` : 'IA')
                          : 'Humano'}
                      </span>
                      <button
                        className={`cweb-ai-switch ${selectedContact.aiActive === 1 ? 'ai-active' : ''}`}
                        onClick={handleToggleAI}
                        disabled={isTogglingAI}
                      >
                        <span className="cweb-ai-switch-slider"></span>
                      </button>
                    </div>
                  )}

                  <button
                    className="cweb-header-button"
                    onClick={() => setShowHeaderMenu(!showHeaderMenu)}
                  >
                    <FiMoreVertical />
                  </button>

                  {showHeaderMenu && (
                    <div className="cweb-header-menu">
                      {canTransferContact() && (
                        <div className="cweb-header-menu-item" onClick={() => setShowTransferModal(true)}>
                          <FiUserPlus />
                          Transferir contato
                        </div>
                      )}
                      <div className="cweb-header-menu-item" onClick={() => setShowTagModal(true)}>
                        <FiTag />
                        Mudar etiqueta
                      </div>
                      <div className="cweb-header-menu-item" onClick={() => setShowArchiveModal(true)}>
                        <FiArchive />
                        {selectedContact?.archived ? 'Desarquivar' : 'Arquivar'}
                      </div>
                      {canDeleteContact() && (
                        <div className="cweb-header-menu-item" onClick={() => setShowDeleteModal(true)}>
                          <FiUsers />
                          Excluir contato
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {visionEnabled && !selectedContact.isGroup && showMemoryPanel && (
                <div style={{ padding: '0 16px 8px' }}>
                  <ContactMemoryPanel
                    contactId={selectedContact.id}
                    contactName={getContactPickerPrimaryLabel(selectedContact.name, selectedContact.number)}
                  />
                </div>
              )}

              {visionEnabled && !selectedContact.isGroup && (
                <InboxAiAssist
                  contactId={selectedContact.id}
                  contactName={getContactPickerPrimaryLabel(selectedContact.name, selectedContact.number)}
                  onUseSuggestion={(text) => (replyChannel === 'email' ? setEmailDraftSeed(text) : setMessageInput(text))}
                  onSendSuggestion={(text) => (replyChannel === 'email' ? setEmailDraftSeed(text) : setMessageInput(text))}
                  onAssume={() => { if (selectedContact.aiActive === 1) void handleToggleAI(); }}
                />
              )}

              {/* Área de mensagens */}
              <div className="cweb-messages" ref={messagesContainerRef}>
                {isMessagesLoading && (
                  <div className="cweb-messages-loading">
                    <LoadingSpinner size="lg" color="primary" />
                    <span>Carregando mensagens…</span>
                  </div>
                )}

                {hasMore && !isMessagesLoading && (
                  <button className="cweb-load-more" onClick={() => loadMessages(selectedContact.id, page + 1, true)}>
                    {isLoadingMore ? (
                      <div className="cweb-loading-spinner" />
                    ) : (
                      'Carregar mensagens anteriores'
                    )}
                  </button>
                )}

                {selectedContact && !selectedContact.isGroup && <EmailHistoryNotice {...contactEmails} />}
                {timeline.map(item => (item.kind === 'email'
                  ? (
                    <EmailTimelineBubble
                      key={item.key}
                      email={item.email}
                      formatTime={(iso) => formatMessageTime(iso)}
                      onReply={emailAvailable ? handleReplyByEmail : undefined}
                    />
                  )
                  : renderMessage(item.message)))}

                <div ref={messagesEndRef} />

                {showScrollToBottom && (
                  <button className="cweb-scroll-to-bottom" onClick={() => scrollToBottom()}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 16l-6-6h12l-6 6z" fill="currentColor" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Área de input */}
              <div className="cweb-input-area">
              <WhatsAppWindowNotice
                active={whatsAppCloudActive && replyChannel === 'whatsapp' && !selectedContact.isGroup}
                messages={messages}
                loading={isMessagesLoading}
                sectorId={selectedSectorId ? Number(selectedSectorId) : null}
                contactId={selectedContact.id}
                contactName={selectedContact.name}
              />
                {!selectedContact.isGroup && (emailAvailable || emailOnly || instagramAvailable || messengerAvailable || webchatAvailable) && (
                  <ReplyChannelSwitch
                    value={replyChannel}
                    onChange={setReplyChannel}
                    whatsappAvailable={whatsappAvailable}
                    emailAvailable={emailAvailable}
                    instagramAvailable={instagramAvailable}
                    messengerAvailable={messengerAvailable}
                    webchatAvailable={webchatAvailable}
                  />
                )}
                {instagramNeedsReconnect && replyChannel !== 'email' && !selectedContact.isGroup ? (
                  <InstagramReconnectNotice />
                ) : replyChannel === 'instagram' && instagramAvailable && !selectedContact.isGroup && selectedSectorId ? (
                  <MetaReplyComposer
                    sectorId={Number(selectedSectorId)}
                    contactId={selectedContact.id}
                    channel="instagram"
                    refreshKey={messages.length > 0 ? messages[messages.length - 1].id : 0}
                    onSent={(result, text) => {
                      mergeRealtimeMessage({
                        id: result.messageId,
                        content: text,
                        mediaType: 'text',
                        mediaUrl: null,
                        fileName: null,
                        mimeType: null,
                        sectorId: Number(selectedSectorId),
                        contactID: selectedContact.id,
                        sentAt: new Date().toISOString(),
                        isSent: true,
                        isRead: false,
                        channel: 'instagram',
                        status: 'sent',
                      });
                      scrollToBottom();
                    }}
                  />
                ) : replyChannel === 'messenger' && messengerAvailable && !selectedContact.isGroup && selectedSectorId ? (
                  <MetaReplyComposer
                    sectorId={Number(selectedSectorId)}
                    contactId={selectedContact.id}
                    channel="messenger"
                    refreshKey={messages.length > 0 ? messages[messages.length - 1].id : 0}
                    onSent={(result, text) => {
                      mergeRealtimeMessage({
                        id: result.messageId,
                        content: text,
                        mediaType: 'text',
                        mediaUrl: null,
                        fileName: null,
                        mimeType: null,
                        sectorId: Number(selectedSectorId),
                        contactID: selectedContact.id,
                        sentAt: new Date().toISOString(),
                        isSent: true,
                        isRead: false,
                        channel: 'messenger',
                        status: 'sent',
                      });
                      scrollToBottom();
                    }}
                  />
                ) : replyChannel === 'webchat' && webchatAvailable && !selectedContact.isGroup && selectedSectorId ? (
                  <MetaReplyComposer
                    sectorId={Number(selectedSectorId)}
                    contactId={selectedContact.id}
                    channel="webchat"
                    refreshKey={messages.length > 0 ? messages[messages.length - 1].id : 0}
                    onSent={(result, text) => {
                      mergeRealtimeMessage({
                        id: result.messageId,
                        content: text,
                        mediaType: 'text',
                        mediaUrl: null,
                        fileName: null,
                        mimeType: null,
                        sectorId: Number(selectedSectorId),
                        contactID: selectedContact.id,
                        sentAt: new Date().toISOString(),
                        isSent: true,
                        isRead: false,
                        channel: 'webchat',
                        status: 'sent',
                      });
                      scrollToBottom();
                    }}
                  />
                ) : replyChannel === 'email' && !selectedContact.isGroup && selectedSectorId ? (
                  <EmailReplyComposer
                    sectorId={Number(selectedSectorId)}
                    contactId={selectedContact.id}
                    status={contactEmails.status}
                    replyTo={emailReplyTo}
                    onClearReplyTo={() => setEmailReplyTo(null)}
                    initialBody={emailDraftSeed}
                    onSent={(item) => { contactEmails.append(item); void contactEmails.reload(); scrollToBottom(); }}
                  />
                ) : isRecording ? (
                  <AudioRecorder
                    onSend={handleSendAudio}
                    onCancel={() => setIsRecording(false)}
                  />
                ) : (
                  <div className="cweb-input-container">
                    <textarea
                      className="cweb-input"
                      placeholder={waComposerLocked
                        ? WHATSAPP_TEMPLATE_ONLY_TEXT
                        : selectedSectorId
                          ? 'Digite uma mensagem…'
                          : 'Selecione uma conexão para enviar mensagens'}
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={handleKeyPress}
                      disabled={!selectedSectorId || waComposerLocked}
                    />

                    <div className="cweb-input-divider"></div>

                    <div className="cweb-input-bottom">
                      <div className="cweb-input-actions">
                        <button
                          className="cweb-input-button"
                          title="Anexar imagem"
                          aria-label="Anexar imagem"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={!selectedSectorId || waComposerLocked}
                        >
                          <ImageIcon />
                        </button>
                        <button
                          className="cweb-input-button"
                          title="Anexar documento"
                          aria-label="Anexar documento"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={!selectedSectorId || waComposerLocked}
                        >
                          <DocumentIcon />
                        </button>
                        <button
                          className="cweb-input-button"
                          title="Gravar áudio"
                          aria-label="Gravar áudio"
                          onClick={() => isMicrophoneAvailable && setIsRecording(true)}
                          disabled={!isMicrophoneAvailable || !selectedSectorId || waComposerLocked}
                        >
                          <MicIcon />
                        </button>
                      </div>

                      <button
                        className="cweb-send-button"
                        onClick={() => messageInput.trim() && handleSendMessage()}
                        disabled={!messageInput.trim() || !selectedSectorId || waComposerLocked}
                      >
                        <SendIcon />
                      </button>
                    </div>

                    {/* Input de arquivo oculto */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,application/*,.pdf,.doc,.docx,.txt"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="cweb-empty">
              <div className="cweb-empty-content">
                <div className="cweb-empty-icon">
                  <FiMessageCircle size={36} color="var(--primary-color)" />
                </div>
                <h3>{visionEnabled ? 'Escolha uma conversa' : 'Selecione um contato para começar a conversa'}</h3>
                <p>
                  {selectedSectorId
                    ? (visionEnabled ? 'Selecione uma conversa na lista ao lado para ler e responder.' : 'Selecione um contato na lista à esquerda para começar a conversar')
                    : (visionEnabled ? 'Selecione uma conexão no menu para ver as conversas.' : 'Selecione uma conexão no menu principal para ver os contatos disponíveis')
                  }
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Modal de transferência */}
        {showTransferModal && (
          <div className="cweb-modal-overlay" onClick={() => setShowTransferModal(false)}>
            <div className="cweb-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cweb-modal-header">
                <h3>Transferir contato</h3>
                <button onClick={() => setShowTransferModal(false)}>×</button>
              </div>
              <div className="cweb-modal-content">
                <p>Escolha para quem transferir o contato <strong>{selectedContact?.name}</strong>:</p>
                <div className="cweb-user-list">
                  {users.filter(user => user.id !== currentUserId).map(user => (
                    <div
                      key={user.id}
                      className={`cweb-user-item ${selectedUserId === user.id ? 'selected' : ''}`}
                      onClick={() => setSelectedUserId(user.id)}
                    >
                      <div className="cweb-user-avatar">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <span>{user.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="cweb-modal-actions">
                <button className="cweb-btn cweb-btn-secondary" onClick={() => setShowTransferModal(false)}>
                  Cancelar
                </button>
                <button
                  className="cweb-btn cweb-btn-primary"
                  onClick={handleTransferContact}
                  disabled={!selectedUserId || isTransferLoading}
                >
                  {isTransferLoading ? 'Transferindo…' : 'Transferir'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de tag */}
        {showTagModal && (
          <div className="cweb-modal-overlay" onClick={() => setShowTagModal(false)}>
            <div className="cweb-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cweb-modal-header">
                <h3>Mudar etiqueta</h3>
                <button onClick={() => setShowTagModal(false)}>×</button>
              </div>
              <div className="cweb-modal-content">
                <p>Escolha a nova etiqueta do contato <strong>{selectedContact?.name}</strong>:</p>
                <div className="cweb-tag-list">
                  <div
                    className={`cweb-tag-item ${selectedTagId === null ? 'selected' : ''}`}
                    onClick={() => setSelectedTagId(null)}
                  >
                    <div className="cweb-tag-color" style={{ backgroundColor: 'var(--bg-elevated-strong)' }}></div>
                    <span>Sem etiqueta</span>
                  </div>
                  {tags.map(tag => (
                    <div
                      key={tag.id}
                      className={`cweb-tag-item ${selectedTagId === tag.id ? 'selected' : ''}`}
                      onClick={() => setSelectedTagId(tag.id)}
                    >
                      <div className="cweb-tag-color" style={{ backgroundColor: tag.color }}></div>
                      <span>{tag.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="cweb-modal-actions">
                <button className="cweb-btn cweb-btn-secondary" onClick={() => setShowTagModal(false)}>
                  Cancelar
                </button>
                <button
                  className="cweb-btn cweb-btn-primary"
                  onClick={handleUpdateTag}
                  disabled={isTagLoading}
                >
                  {isTagLoading ? 'Salvando…' : 'Mudar etiqueta'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ConfirmModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteContact}
          title="Excluir contato"
          message={selectedContact ? `Remover ${selectedContact.name} desta lista? Isso é diferente de arquivar a conversa.` : ''}
          confirmLabel="Excluir"
          cancelLabel="Cancelar"
          variant="danger"
          loading={isDeletingContact}
        />
        <ConfirmModal
          open={showArchiveModal}
          onClose={() => setShowArchiveModal(false)}
          onConfirm={handleArchiveContact}
          title={selectedContact?.archived ? 'Desarquivar contato' : 'Arquivar contato'}
          message={selectedContact ? `Quer mesmo ${selectedContact.archived ? 'desarquivar' : 'arquivar'} o contato ${selectedContact.name}?${!selectedContact.archived ? ' Ele vai para a seção de arquivadas.' : ''}` : ''}
          confirmLabel={selectedContact?.archived ? 'Desarquivar' : 'Arquivar'}
          cancelLabel="Cancelar"
          variant="default"
          loading={isArchivingContact}
        />

        {/* Modal de novo contato */}
        {showNewContactModal && (
          <div className="cweb-modal-overlay" onClick={() => setShowNewContactModal(false)}>
            <div className="cweb-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cweb-modal-header">
                <h3>Novo contato</h3>
                <button onClick={() => setShowNewContactModal(false)}>×</button>
              </div>
              <div className="cweb-modal-content">
                <div className="cweb-form-group">
                  <label>Nome *</label>
                  <input
                    type="text"
                    value={newContactName}
                    onChange={(e) => setNewContactName(e.target.value)}
                    placeholder="Nome do contato"
                  />
                </div>
                <div className="cweb-form-group">
                  <label>Telefone *</label>
                  <input
                    type="text"
                    value={newContactNumber}
                    onChange={(e) => setNewContactNumber(e.target.value)}
                    placeholder="Número do WhatsApp com DDD"
                  />
                </div>
                {newContactError && (
                  <div className="cweb-error">{newContactError}</div>
                )}
              </div>
              <div className="cweb-modal-actions">
                <button className="cweb-btn cweb-btn-secondary" onClick={() => setShowNewContactModal(false)}>
                  Cancelar
                </button>
                <button
                  className="cweb-btn cweb-btn-primary"
                  onClick={handleCreateContact}
                  disabled={isCreatingContact}
                >
                  {isCreatingContact ? 'Criando…' : 'Criar contato'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Modal de Filtros */}
        {showFiltersModal && (
          <div className="cweb-modal-overlay" onClick={() => setShowFiltersModal(false)}>
            <div className="cweb-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cweb-modal-header">
                <h3>Filtros</h3>
                <button onClick={() => setShowFiltersModal(false)}>×</button>
              </div>
              <div className="cweb-modal-content">
                <div className="cweb-form-group">
                  <label>Status</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="cweb-select"
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                  >
                    <option value="all">Todos os status</option>
                    <option value="Novo">Novo</option>
                    <option value="Qualificado">Qualificado</option>
                    <option value="Não qualificado">Não qualificado</option>
                  </select>
                </div>
              </div>
              <div className="cweb-modal-actions">
                <button className="cweb-btn cweb-btn-secondary" onClick={() => setShowFiltersModal(false)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        {showGroupModal && selectedContactForGroup && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000
          }}>
            <div style={{ backgroundColor: 'var(--bg-card)', padding: '20px', borderRadius: '8px', width: '300px', maxHeight: '80vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3>Gerenciar grupos</h3>
                <FiX style={{ cursor: 'pointer' }} onClick={() => setShowGroupModal(false)} />
              </div>
              <p style={{ marginBottom: '10px' }}>{selectedContactForGroup.name}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {internalGroups.map(group => (
                  <label key={group.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={contactGroups.includes(group.id)}
                      onChange={() => toggleGroupMembership(group.id)}
                    />
                    {group.name}
                  </label>
                ))}
                {internalGroups.length === 0 && <p>Nenhum grupo criado.</p>}
              </div>
              <div style={{ marginTop: '15px', textAlign: 'right' }}>
                <button
                  onClick={() => setShowGroupModal(false)}
                  style={{ padding: '8px 16px', backgroundColor: '#25D366', color: 'var(--text-primary)', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Concluir
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Context Menu */}
        <ContactContextMenu
          {...contextMenu}
          onClose={closeContextMenu}
          onArchive={handleArchiveFromMenu}
          onFavorite={handleFavoriteFromMenu}
          onAddToGroup={handleOpenGroupModal}
          isArchived={contextMenu.contactId ? contacts.find(c => c.id === contextMenu.contactId)?.archived : false}
          isFavorite={contextMenu.contactId ? contacts.find(c => c.id === contextMenu.contactId)?.isFavorite : false}
        />
      </div>
    </div>
  );
};

export default ChatWeb; 